<template>
  <vue-element-loading
    :active="loading"
    spinner="bar-fade-scale"
    color="#1E660B"
  />
  <div class="h-full">
  <div class="flex flex-wrap items-center mt-4 lg:mt-8 mb-2 lg:mb-4 sticky top-0 z-20 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-gray-200 px-1 pt-4 pb-3 relative">
      <!-- title -->
      <div class="order-1 flex-grow flex items-center mb-3 md:mb-4 pr-16 md:pr-0">
        <svg-icon
          type="mdi"
          :path="icons.tasks"
          :size="32"
          class="text-navy"
        ></svg-icon>

        <span class="ml-2 text-navy font-bold leading-7 text-2xl"> Tasks </span>
      </div>

      <!-- Right side actions (grouped together next to Add button) -->
      <div class="order-2 flex items-center space-x-3 mb-2 md:mb-0 md:ml-auto">
        <div v-if="displayedIssuesCount > 0" class="relative hidden md:block">
          <button
            id="issues_toggle_top"
            type="button"
            @click="loadTaskWithIssues"
            :aria-pressed="issueMode ? 'true' : 'false'"
            class="h-8 px-3 inline-flex items-center rounded-md border text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light transition-colors"
            :class="issueMode ? 'bg-gray-300 border-gray-300 text-gray-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1 text-gray-600" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a6 6 0 00-6 6c0 1.887-.454 3.665-1.257 5.234a.75.75 0 00.515 1.07 32.903 32.903 0 003.256.508 3.5 3.5 0 006.972 0 32.903 32.903 0 003.256-.508.75.75 0 00.515-1.07C16.454 11.665 16 9.887 16 8a6 6 0 00-6-6zM8.05 14.943a33.54 33.54 0 003.9 0 2 2 0 01-3.9 0z"/></svg>
            <span class="font-medium leading-none">Notifications</span>
            <span
              class="ml-2 inline-flex items-center justify-center min-w-[1.1rem] h-5 px-1 rounded-full text-[10px] font-semibold text-white"
              :class="sample_path_issues > 0 ? 'bg-red-600' : 'bg-yellow-600'"
            >{{ displayedIssuesCount }}</span>
          </button>
        </div>
        <!-- Filters group -->
        <div class="flex items-center">
          <button
            :class="['relative h-8 pl-3 pr-2 inline-flex items-center rounded-md border text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light transition-colors', totalActiveCount>0 ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50']"
            ref="taskFiltersButton"
            type="button"
            @click="openFilters"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1 text-gray-600" viewBox="0 0 20 20" fill="currentColor"><path d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.8 6.4V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-3.999L3.2 4.6A1 1 0 013 4z"/></svg>
            <span class="leading-none">Search & Filters</span>
            <span v-if="totalActiveCount > 0" class="ml-2 inline-flex items-center justify-center min-w-[1.1rem] h-5 px-1 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">{{ totalActiveCount }}</span>
          </button>
          <!-- Inline clear icon (no layout shift) -->
          <button
            v-if="totalActiveCount > 0"
            @click.stop="clearAllFilters"
            type="button"
            aria-label="Clear all filters"
            class="ml-1 h-6 w-6 inline-flex items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-400 text-xs font-bold"
          >
            ×
          </button>
        </div>
      </div>
      <!-- CSV Import button -->
      <div v-if="role == 'admin'" class="order-3 mb-2 md:mb-0 ml-3">
        <inertia-link
          class="flex items-center justify-center w-9 h-9 rounded-full text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow transition-colors"
          :href="route('app.soil-analysis.csv-import.index')"
        >
          <svg-icon type="mdi" size="20" :path="icons.csvImport" class="text-white" />
          <span class="sr-only"> CSV Import </span>
        </inertia-link>
      </div>
      <!-- Add button -->
      <div
        class="order-3 mb-2 md:mb-0 ml-3 md:ml-4"
        v-if="['admin', 'consultant', 'consultant_user', 'contractor'].includes(role)"
      >
        <inertia-link
          class="flex items-center justify-center w-9 h-9 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light shadow"
          :href="route('app.task.create')"
        >
          <svg
            viewBox="0 0 40 40"
            xmlns="http://www.w3.org/2000/svg"
            class="block w-5 h-5 stroke-current text-white"
          >
            <g transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)">
              <path d="M12 7.5L12 16.5" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="3"></path>
              <path d="M7.5 12L16.5 12" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="3"></path>
            </g>
          </svg>
          <span class="sr-only"> Add Task </span>
        </inertia-link>
      </div>
    </div>

    <!-- Mobile Tab Navigation (visible only on mobile) -->
    <div class="md:hidden mb-4 px-1">
      <div class="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          @click="setActiveTab('dashboard')"
          :class="[
            'flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors',
            activeTab === 'dashboard'
              ? 'bg-white text-navy shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          <div class="flex items-center justify-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
            Dashboard
          </div>
        </button>
        <button
          @click="setActiveTab('tasks')"
          :class="[
            'flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors',
            activeTab === 'tasks'
              ? 'bg-white text-navy shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          <div class="flex items-center justify-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
            </svg>
            Tasks
          </div>
        </button>
      </div>
    </div>

    <!-- Mobile Tasks Content -->
    <div v-if="activeTab === 'tasks' && !issueMode" class="md:hidden px-1">
      <!-- Empty state -->
      <div v-if="tasks.length === 0" class="bg-white mt-8 px-6 py-4 text-center">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
        <p class="mt-1 text-sm text-gray-500">Try adjusting your search filters.</p>
      </div>

      <!-- Mobile task cards -->
      <div v-if="tasks.length > 0" class="space-y-3">
        <div
          v-for="(task, index) in tasks"
          :key="'mcard-'+index"
          class="bg-white rounded-lg border border-gray-200 p-3 flex flex-col gap-2 relative shadow-sm"
        >
          <!-- Top row: Farmer / Task / Actions -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0">
                <h3 class="text-sm font-semibold text-navy truncate"
                    :title="$page.props.auth.user.role === 'farm_owner' ? taskTypeLabel(task) : task.farmer_name">
                  {{ $page.props.auth.user.role === 'farm_owner' ? taskTypeLabel(task) : task.farmer_name }}
                </h3>
                <span class="text-[11px] font-mono text-gray-500 truncate" :title="task.task_number">#{{ task.task_number }}</span>
              </div>
              <div class="mt-1 flex items-center gap-2 flex-wrap">
                <span :class="[...statusPillClass(task), 'px-2 py-0.5 text-[11px]']">{{ task.status }}</span>
                <div
                  v-if="task.sample_path_issues > 0 || task.sample_zone_issues > 0"
                  class="w-5 h-5 text-center text-white text-[10px] leading-5 rounded-full"
                  :class="task.issues_class"
                  :title="task.issues_count + ' open issue'+(task.issues_count===1?'':'s')"
                >
                  {{ task.issues_count }}
                </div>
                <div v-if="hasReports(task)" class="flex items-stretch rounded overflow-hidden w-7 h-4 border border-gray-300" :title="getReportsTooltip(task)">
                  <div class="flex-1" :class="getTaskReportClass(task)"></div>
                  <div class="w-px bg-white/80"></div>
                  <div class="flex-1" :class="getSoilAnalysisClass(task)"></div>
                </div>
              </div>
            </div>
            <div class="shrink-0">
              <index-menu-item :links="indexLinks(task)" />
            </div>
          </div>

          <!-- Meta rows -->
          <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px] leading-tight text-gray-700">
            <div v-if="['admin','contractor','lab_user'].includes(role) || $page.props.auth.user.organisation?.type === 'lab'">
              <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Requester</p>
              <p class="truncate" :title="task.advisor">{{ task.advisor || '—' }}</p>
            </div>
            <div v-if="['admin','contractor','consultant','consultant_user','lab_user'].includes(role)">
              <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Assigned</p>
              <p class="truncate" :title="task.assigned_to">{{ task.assigned_to || '—' }}</p>
            </div>
            <div>
              <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Created</p>
              <p class="truncate" :title="task.date">{{ task.date || '—' }}</p>
            </div>
            <div>
              <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Collected</p>
              <p class="truncate" :title="task.collected">{{ task.collected || '—' }}</p>
            </div>
            <div>
              <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Samples</p>
              <p class="truncate" :title="task.samples_count + ' samples'">{{ task.samples_count || 0 }}</p>
            </div>
            <div class="col-span-2" v-if="hasReports(task)">
              <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Reports</p>
              <div class="flex items-center gap-2">
                <div class="flex items-stretch rounded overflow-hidden w-10 h-4 border border-gray-300" :title="getReportsTooltip(task)">
                  <div class="flex-1" :class="getTaskReportClass(task)"></div>
                  <div class="w-px bg-white/80"></div>
                  <div class="flex-1" :class="getSoilAnalysisClass(task)"></div>
                </div>
                <span class="text-[10px] text-gray-500">Task / Soil</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Created Date Filter Display -->
    <div class="-mt-2 flex justify-end px-1">
      <span class="text-sm text-gray-600">
        Showing Created Date: <strong>{{ currentCreatedDateLabel }}</strong>
      </span>
    </div>

    <!-- Mobile Dashboard Tab Content -->
    <div v-if="activeTab === 'dashboard'" class="md:hidden mb-6 px-1">
      <div v-if="dashboardStats">
        <!-- Overall Summary Cards -->
        <div class="mb-4">
          <div class="grid grid-cols-1 gap-3">
            <!-- Total Tasks -->  
            <div class="group relative overflow-hidden bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 p-4 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all duration-200 cursor-default">
              <div class="relative flex items-center space-x-4">
                <div class="flex-shrink-0 w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center group-hover:bg-indigo-200 transition-colors duration-200">
                  <svg class="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-3xl font-bold text-indigo-700 leading-tight">{{ localDashboardStats.total_tasks }}</div>
                  <div class="text-sm font-medium text-indigo-600 uppercase tracking-wide">Total Tasks</div>
                </div>
              </div>
            </div>
            
            <!-- Total Samples -->
            <div class="group relative overflow-hidden bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 p-4 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all duration-200 cursor-default">
              <div class="relative flex items-center space-x-4">
                <div class="flex-shrink-0 w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center group-hover:bg-indigo-200 transition-colors duration-200">
                  <svg class="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-3xl font-bold text-indigo-700 leading-tight">{{ localDashboardStats.total_samples }}</div>
                  <div class="text-sm font-medium text-indigo-600 uppercase tracking-wide">Total Samples</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Task Status Breakdown -->
        <div class="mb-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Task Status Overview</h3>
          <div class="grid grid-cols-2 gap-3">
          
          <!-- Not Started -->
          <div class="group relative overflow-hidden bg-white rounded-lg border border-red-200 p-3 shadow-sm hover:shadow-md hover:border-red-300 transition-all duration-200 cursor-default">
            <div class="absolute inset-0 bg-gradient-to-br from-red-50/50 to-red-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div class="relative flex items-center space-x-3">
              <div class="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors duration-200">
                <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-xl font-bold text-red-700 leading-tight">{{ localDashboardStats.not_started }}</div>
                <div class="text-xs font-medium text-red-600 uppercase tracking-wide">Not Started</div>
              </div>
            </div>
          </div>
          
          <!-- In Progress -->
          <div class="group relative overflow-hidden bg-white rounded-lg border border-amber-200 p-3 shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-200 cursor-default">
            <div class="absolute inset-0 bg-gradient-to-br from-amber-50/50 to-amber-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div class="relative flex items-center space-x-3">
              <div class="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center group-hover:bg-amber-200 transition-colors duration-200">
                <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-xl font-bold text-amber-700 leading-tight">{{ localDashboardStats.in_progress }}</div>
                <div class="text-xs font-medium text-amber-600 uppercase tracking-wide">In Progress</div>
              </div>
            </div>
          </div>
          
          <!-- Collected -->
          <div class="group relative overflow-hidden bg-white rounded-lg border border-blue-200 p-3 shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-default">
            <div class="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-blue-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div class="relative flex items-center space-x-3">
              <div class="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-200">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-xl font-bold text-blue-700 leading-tight">{{ localDashboardStats.collected }}</div>
                <div class="text-xs font-medium text-blue-600 uppercase tracking-wide">Collected</div>
              </div>
            </div>
          </div>
          
          <!-- Lab -->
          <div class="group relative overflow-hidden bg-white rounded-lg border border-purple-200 p-3 shadow-sm hover:shadow-md hover:border-purple-300 transition-all duration-200 cursor-default">
            <div class="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-purple-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div class="relative flex items-center space-x-3">
              <div class="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors duration-200">
                <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5z"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-xl font-bold text-purple-700 leading-tight">{{ localDashboardStats.lab }}</div>
                <div class="text-xs font-medium text-purple-600 uppercase tracking-wide">At Lab</div>
              </div>
            </div>
          </div>
          
          <!-- Complete -->
          <div class="group relative overflow-hidden bg-white rounded-lg border border-emerald-200 p-3 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all duration-200 cursor-default">
            <div class="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-emerald-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div class="relative flex items-center space-x-3">
              <div class="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors duration-200">
                <svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-xl font-bold text-emerald-700 leading-tight">{{ localDashboardStats.complete }}</div>
                <div class="text-xs font-medium text-emerald-600 uppercase tracking-wide">Complete</div>
              </div>
            </div>
          </div>
          
          <!-- Issues -->
          <div class="group relative overflow-hidden bg-white rounded-lg border border-orange-200 p-3 shadow-sm hover:shadow-md hover:border-orange-300 transition-all duration-200 cursor-default">
            <div class="absolute inset-0 bg-gradient-to-br from-orange-50/50 to-orange-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div class="relative flex items-center space-x-3">
              <div class="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors duration-200">
                <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-xl font-bold text-orange-700 leading-tight">{{ localDashboardStats.with_issues }}</div>
                <div class="text-xs font-medium text-orange-600 uppercase tracking-wide">Issues</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Desktop Dashboard Stats Cards (hidden on mobile) -->
    <div v-if="localDashboardStats" class="mb-6 px-1 hidden md:block">
      <!-- Overall Summary Cards -->
      <div class="mb-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <!-- Total Tasks -->  
          <div class="group relative overflow-hidden bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 p-4 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all duration-200 cursor-default">
            <div class="relative flex items-center space-x-4">
              <div class="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center group-hover:bg-indigo-200 transition-colors duration-200">
                <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-2xl font-bold text-indigo-700 leading-tight">{{ localDashboardStats.total_tasks }}</div>
                <div class="text-xs font-medium text-indigo-600 uppercase tracking-wide">Total Tasks</div>
              </div>
            </div>
          </div>
          
          <!-- Total Samples -->
          <div class="group relative overflow-hidden bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 p-4 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all duration-200 cursor-default">
            <div class="relative flex items-center space-x-4">
              <div class="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center group-hover:bg-indigo-200 transition-colors duration-200">
                <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-2xl font-bold text-indigo-700 leading-tight">{{ localDashboardStats.total_samples }}</div>
                <div class="text-xs font-medium text-indigo-600 uppercase tracking-wide">Total Samples</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Task Status Breakdown -->
      <div class="mb-4">
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        
        <!-- Not Started -->
        <div class="group relative overflow-hidden bg-white rounded-lg border border-red-200 p-3 shadow-sm hover:shadow-md hover:border-red-300 transition-all duration-200 cursor-default">
          <div class="absolute inset-0 bg-gradient-to-br from-red-50/50 to-red-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          <div class="relative flex items-center space-x-3">
            <div class="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors duration-200">
              <svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-lg font-bold text-red-700 leading-tight">{{ localDashboardStats.not_started }}</div>
              <div class="text-[10px] font-medium text-red-600 uppercase tracking-wide">Not Started</div>
            </div>
          </div>
        </div>
        
        <!-- In Progress -->
        <div class="group relative overflow-hidden bg-white rounded-lg border border-amber-200 p-3 shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-200 cursor-default">
          <div class="absolute inset-0 bg-gradient-to-br from-amber-50/50 to-amber-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          <div class="relative flex items-center space-x-3">
            <div class="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center group-hover:bg-amber-200 transition-colors duration-200">
              <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-lg font-bold text-amber-700 leading-tight">{{ localDashboardStats.in_progress }}</div>
              <div class="text-[10px] font-medium text-amber-600 uppercase tracking-wide">In Progress</div>
            </div>
          </div>
        </div>
        
        <!-- Collected -->
        <div class="group relative overflow-hidden bg-white rounded-lg border border-blue-200 p-3 shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-default">
          <div class="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-blue-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          <div class="relative flex items-center space-x-3">
            <div class="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-200">
              <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-lg font-bold text-blue-700 leading-tight">{{ localDashboardStats.collected }}</div>
              <div class="text-[10px] font-medium text-blue-600 uppercase tracking-wide">Collected</div>
            </div>
          </div>
        </div>
        
        <!-- Lab -->
        <div class="group relative overflow-hidden bg-white rounded-lg border border-purple-200 p-3 shadow-sm hover:shadow-md hover:border-purple-300 transition-all duration-200 cursor-default">
          <div class="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-purple-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          <div class="relative flex items-center space-x-3">
            <div class="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors duration-200">
              <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5z"></path>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-lg font-bold text-purple-700 leading-tight">{{ localDashboardStats.lab }}</div>
              <div class="text-[10px] font-medium text-purple-600 uppercase tracking-wide">At Lab</div>
            </div>
          </div>
        </div>
        
        <!-- Complete -->
        <div class="group relative overflow-hidden bg-white rounded-lg border border-emerald-200 p-3 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all duration-200 cursor-default">
          <div class="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-emerald-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          <div class="relative flex items-center space-x-3">
            <div class="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors duration-200">
              <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-lg font-bold text-emerald-700 leading-tight">{{ localDashboardStats.complete }}</div>
              <div class="text-[10px] font-medium text-emerald-600 uppercase tracking-wide">Complete</div>
            </div>
          </div>
        </div>
        
        <!-- Issues -->
        <div class="group relative overflow-hidden bg-white rounded-lg border border-orange-200 p-3 shadow-sm hover:shadow-md hover:border-orange-300 transition-all duration-200 cursor-default">
          <div class="absolute inset-0 bg-gradient-to-br from-orange-50/50 to-orange-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          <div class="relative flex items-center space-x-3">
            <div class="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors duration-200">
              <svg class="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-lg font-bold text-orange-700 leading-tight">{{ localDashboardStats.with_issues }}</div>
              <div class="text-[10px] font-medium text-orange-600 uppercase tracking-wide">Issues</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- (Removed separate Filters Trigger section - now integrated in header) -->

    <!-- Filter Drawer Component - Using Teleport to render outside the layout container -->
    <Teleport to="body">
      <FilterDrawer :show="showFilters" title="Search & Filters" :active-count="totalActiveCount" :no-backdrop="true" :hide-apply-button="true" @close="closeFilters" @clear="clearDrawerFilters">
      <div class="space-y-4">
        {{ console.log('FilterDrawer content rendering, showFilters:', showFilters) }}
        <!-- Search Section -->
        <div class="pb-4 border-b border-gray-200">
          <h3 class="text-sm font-medium text-gray-900 mb-3">Search</h3>
          <div class="space-y-3">
            <SearchField
              label="Farmer Name"
              placeholder="Enter farmer name"
              v-model="searchFields.farmer_name"
              @search="handleSearchFieldChange"
            />
            <SearchField
              label="Task Number"
              placeholder="Enter task number"
              v-model="searchFields.task_number"
              @search="handleSearchFieldChange"
            />
            <SearchField
              label="Sample ID"
              placeholder="Enter sample ID"
              v-model="searchFields.sample_id"
              @search="handleSearchFieldChange"
            />
          </div>
        </div>
        
        <!-- Filters Section -->
        <div>
          <h3 class="text-sm font-medium text-gray-900 mb-3">Filters</h3>
          <div class="space-y-3">
        <!-- Created Date first (primary filter) -->
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Created Date</label>
          <VueDatePicker
            v-model="taskDraft.created_date"
            @update:model-value="handleFilterChange"
            :range="dateModes.created === 'range'"
            :month-picker="dateModes.created === 'month'"
            :teleport="true"
            :enable-time-picker="false"
            auto-apply
            :text-input="{ format: 'dd/MM/yyyy - dd/MM/yyyy' }"
            :format="formatCreated"
            rangeSeparator="-"
            placeholder="Filter By Created Date"
          >
            <template #left-sidebar>
              <div class="p-2 w-40 text-xs space-y-3">
                <div>
                  <p class="font-semibold mb-1">Presets</p>
                  <ul class="space-y-1">
                    <li v-for="p in datePresets" :key="p.label">
                      <button type="button" @click="applyPreset('created', p.range)" class="w-full text-left px-2 py-1 rounded hover:bg-indigo-50" :class="isPresetActive('created', p) ? 'bg-indigo-600 text-white hover:bg-indigo-600' : ''">{{ p.label }}</button>
                    </li>
                  </ul>
                </div>
                <div>
                  <p class="font-semibold mb-1">Mode</p>
                  <div class="flex flex-wrap gap-1">
                    <button v-for="m in ['range','month']" :key="m" type="button" @click="setDateMode('created', m)" class="px-2 py-0.5 rounded border text-[11px]" :class="dateModes.created===m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'">{{ m==='range' ? 'Range' : 'Month' }}</button>
                  </div>
                  <button v-if="taskDraft.created_date" type="button" @click="taskDraft.created_date=null; handleFilterChange()" class="mt-2 text-red-600 hover:underline">Clear</button>
                </div>
              </div>
            </template>
          </VueDatePicker>
        </div>
        <!-- Organisation (admin) -->
        <div v-if="role === 'admin'">
          <label class="block text-xs font-medium text-gray-700 mb-1" for="advisor_dd_org">Organisation</label>
          <div class="relative">
            <button
              type="button"
              @click="advisorDropdownOpen = !advisorDropdownOpen"
              class="relative w-full bg-gray-200 border border-gray-400 rounded-lg pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:border-gray-400 focus:ring-0 sm:text-sm min-h-[2.5rem]"
            >
              <!-- Selected organisations display -->
              <div v-if="selectedAdvisorNames.length > 0" class="flex flex-wrap gap-1">
                <span 
                  v-for="name in selectedAdvisorNames.slice(0, 2)" 
                  :key="name"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {{ name }}
                </span>
                <span 
                  v-if="selectedAdvisorNames.length > 2"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                >
                  +{{ selectedAdvisorNames.length - 2 }} more
                </span>
              </div>
              <!-- Search input when dropdown is open or placeholder when closed -->
              <input
                v-if="advisorDropdownOpen || selectedAdvisorNames.length === 0"
                type="text"
                v-model="advisorSearch"
                @input="advisorDropdownOpen = true"
                @keydown.escape="advisorDropdownOpen = false"
                :placeholder="selectedAdvisorNames.length === 0 ? 'All Organisations' : 'Search...'"
                class="block w-full p-0 border-none bg-transparent placeholder-gray-500 focus:ring-0 focus:outline-none"
              />
              <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                </svg>
              </span>
            </button>
            
            <!-- Clear button -->
            <button
              v-if="taskDraft.advisor && taskDraft.advisor.length > 0"
              @click="clearAdvisorSelection"
              type="button"
              class="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <!-- Dropdown -->
            <ul
              v-if="advisorDropdownOpen"
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              <li
                v-for="option in filteredAdvisorOptions"
                :key="option.value"
                @click="toggleAdvisorSelection(option)"
                class="cursor-pointer select-none relative py-2 pl-8 pr-4 text-gray-900 hover:bg-gray-50"
              >
                <!-- Checkbox -->
                <div class="absolute left-2 top-1/2 transform -translate-y-1/2">
                  <input
                    type="checkbox"
                    :checked="isAdvisorSelected(option.value)"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    readonly
                  />
                </div>
                <span class="block truncate">{{ option.label }}</span>
              </li>
              <li v-if="filteredAdvisorOptions.length === 0" class="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
                No results found
              </li>
            </ul>
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1" for="status_filter_dd">Status</label>
          <div class="relative">
            <button
              type="button"
              @click="statusDropdownOpen = !statusDropdownOpen"
              class="relative w-full bg-gray-200 border border-gray-400 rounded-lg pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:border-gray-400 focus:ring-0 sm:text-sm min-h-[2.5rem]"
            >
              <!-- Selected statuses display -->
              <div v-if="selectedStatusNames.length > 0" class="flex flex-wrap gap-1">
                <span 
                  v-for="name in selectedStatusNames.slice(0, 2)" 
                  :key="name"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                >
                  {{ name }}
                </span>
                <span 
                  v-if="selectedStatusNames.length > 2"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                >
                  +{{ selectedStatusNames.length - 2 }} more
                </span>
              </div>
              <!-- Search input when dropdown is open or placeholder when closed -->
              <input
                v-if="statusDropdownOpen || selectedStatusNames.length === 0"
                type="text"
                v-model="statusSearch"
                @input="statusDropdownOpen = true"
                @keydown.escape="statusDropdownOpen = false"
                :placeholder="selectedStatusNames.length === 0 ? 'All Statuses' : 'Search...'"
                class="block w-full p-0 border-none bg-transparent placeholder-gray-500 focus:ring-0 focus:outline-none"
              />
              <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                </svg>
              </span>
            </button>
            
            <!-- Clear button -->
            <button
              v-if="taskDraft.status && taskDraft.status.length > 0"
              @click="clearStatusSelection"
              type="button"
              class="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <!-- Dropdown -->
            <ul
              v-if="statusDropdownOpen"
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              <li
                v-for="option in filteredStatusOptions"
                :key="option.value"
                @click="toggleStatusSelection(option)"
                class="cursor-pointer select-none relative py-2 pl-8 pr-4 text-gray-900 hover:bg-gray-50"
              >
                <!-- Checkbox -->
                <div class="absolute left-2 top-1/2 transform -translate-y-1/2">
                  <input
                    type="checkbox"
                    :checked="isStatusSelected(option.value)"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    readonly
                  />
                </div>
                <span class="block truncate">{{ option.label }}</span>
              </li>
              <li v-if="filteredStatusOptions.length === 0" class="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
                No results found
              </li>
            </ul>
          </div>
        </div>
        <div v-if="role !== 'admin'">
          <label class="block text-xs font-medium text-gray-700 mb-1" for="scope_dd">Task Scope</label>
          <select id="scope_dd" v-model="taskDraft.filter" @change="handleFilterChange" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
            <option value="">All Tasks</option>
            <option v-for="(sort, key) in userFilter" :value="key" :key="key">{{ sort }}</option>
          </select>
        </div>
        <div v-if="role !== 'admin' && ['contractor','consultant','consultant_user','lab_user'].includes(role)">
          <label class="block text-xs font-medium text-gray-700 mb-1" for="advisor_dd_other">Requester</label>
          <div class="relative">
            <button
              type="button"
              @click="advisorDropdownOpen = !advisorDropdownOpen"
              class="relative w-full bg-gray-200 border border-gray-400 rounded-lg pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:border-gray-400 focus:ring-0 sm:text-sm min-h-[2.5rem]"
            >
              <!-- Selected requesters display -->
              <div v-if="selectedAdvisorNames.length > 0" class="flex flex-wrap gap-1">
                <span 
                  v-for="name in selectedAdvisorNames.slice(0, 2)" 
                  :key="name"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {{ name }}
                </span>
                <span 
                  v-if="selectedAdvisorNames.length > 2"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                >
                  +{{ selectedAdvisorNames.length - 2 }} more
                </span>
              </div>
              <!-- Search input when dropdown is open or placeholder when closed -->
              <input
                v-if="advisorDropdownOpen || selectedAdvisorNames.length === 0"
                type="text"
                v-model="advisorSearch"
                @input="advisorDropdownOpen = true"
                @keydown.escape="advisorDropdownOpen = false"
                :placeholder="selectedAdvisorNames.length === 0 ? 'Requester' : 'Search...'"
                class="block w-full p-0 border-none bg-transparent placeholder-gray-500 focus:ring-0 focus:outline-none"
              />
              <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                </svg>
              </span>
            </button>
            
            <!-- Clear button -->
            <button
              v-if="taskDraft.advisor && taskDraft.advisor.length > 0"
              @click="clearAdvisorSelection"
              type="button"
              class="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <!-- Dropdown -->
            <ul
              v-if="advisorDropdownOpen"
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              <li
                v-for="option in filteredAdvisorOptions"
                :key="option.value"
                @click="toggleAdvisorSelection(option)"
                class="cursor-pointer select-none relative py-2 pl-8 pr-4 text-gray-900 hover:bg-gray-50"
              >
                <!-- Checkbox -->
                <div class="absolute left-2 top-1/2 transform -translate-y-1/2">
                  <input
                    type="checkbox"
                    :checked="isAdvisorSelected(option.value)"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    readonly
                  />
                </div>
                <span class="block truncate">{{ option.label }}</span>
              </li>
              <li v-if="filteredAdvisorOptions.length === 0" class="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
                No results found
              </li>
            </ul>
          </div>
        </div>
        <div v-if="role === 'admin'">
          <label class="block text-xs font-medium text-gray-700 mb-1" for="consultant_dd">Consultant</label>
            <select id="consultant_dd" v-model="taskDraft.user_filter" @change="handleFilterChange" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
              <option value="">All</option>
              <option v-for="(consultant, key) in consultants" :key="key" :value="consultant.id">{{ consultant.first_name + ' ' + consultant.last_name }}</option>
            </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1" for="assigned_dd">Assigned To</label>
          <div class="relative">
            <button
              type="button"
              @click="contractorDropdownOpen = !contractorDropdownOpen"
              class="relative w-full bg-gray-200 border border-gray-400 rounded-lg pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:border-gray-400 focus:ring-0 sm:text-sm"
            >
              <input
                type="text"
                v-model="contractorSearch"
                @input="contractorDropdownOpen = true"
                @keydown.escape="contractorDropdownOpen = false"
                :placeholder="selectedContractorName || 'All'"
                class="block w-full p-0 border-none bg-transparent placeholder-gray-500 focus:ring-0 focus:outline-none"
              />
              <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                </svg>
              </span>
            </button>
            
            <!-- Clear button -->
            <button
              v-if="taskDraft.contractor"
              @click="clearContractorSelection"
              type="button"
              class="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <!-- Dropdown -->
            <ul
              v-if="contractorDropdownOpen"
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              <li
                v-for="option in filteredContractorOptions"
                :key="option.value"
                @click="selectContractor(option)"
                class="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900 hover:bg-gray-50"
              >
                <span class="block truncate">{{ option.label }}</span>
              </li>
              <li v-if="filteredContractorOptions.length === 0" class="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
                No results found
              </li>
            </ul>
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Active Date</label>
          <VueDatePicker
            v-model="taskDraft.date"
            @update:model-value="handleFilterChange"
            :range="dateModes.active === 'range'"
            :month-picker="dateModes.active === 'month'"
            :teleport="true"
            :enable-time-picker="false"
            auto-apply
            :text-input="{ format: 'dd/MM/yyyy - dd/MM/yyyy' }"
            :format="formatActive"
            rangeSeparator="-"
            placeholder="Filter By Active Date"
          >
            <template #left-sidebar>
              <div class="p-2 w-40 text-xs space-y-3">
                <div>
                  <p class="font-semibold mb-1">Presets</p>
                  <ul class="space-y-1">
                    <li v-for="p in datePresets" :key="p.label">
                      <button type="button" @click="applyPreset('active', p.range)" class="w-full text-left px-2 py-1 rounded hover:bg-indigo-50" :class="isPresetActive('active', p) ? 'bg-indigo-600 text-white hover:bg-indigo-600' : ''">{{ p.label }}</button>
                    </li>
                  </ul>
                </div>
                <div>
                  <p class="font-semibold mb-1">Mode</p>
                  <div class="flex flex-wrap gap-1">
                    <button v-for="m in ['range','month']" :key="m" type="button" @click="setDateMode('active', m)" class="px-2 py-0.5 rounded border text-[11px]" :class="dateModes.active===m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'">{{ m==='range' ? 'Range' : 'Month' }}</button>
                  </div>
                  <button v-if="taskDraft.date" type="button" @click="taskDraft.date=null; handleFilterChange()" class="mt-2 text-red-600 hover:underline">Clear</button>
                </div>
              </div>
            </template>
          </VueDatePicker>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Collected Date</label>
          <VueDatePicker
            v-model="taskDraft.collected_date"
            @update:model-value="handleFilterChange"
            :range="dateModes.collected === 'range'"
            :month-picker="dateModes.collected === 'month'"
            :teleport="true"
            :enable-time-picker="false"
            auto-apply
            :text-input="{ format: 'dd/MM/yyyy - dd/MM/yyyy' }"
            :format="formatCollected"
            rangeSeparator="-"
            placeholder="Filter By Collected Date"
          >
            <template #left-sidebar>
              <div class="p-2 w-40 text-xs space-y-3">
                <div>
                  <p class="font-semibold mb-1">Presets</p>
                  <ul class="space-y-1">
                    <li v-for="p in datePresets" :key="p.label">
                      <button type="button" @click="applyPreset('collected', p.range)" class="w-full text-left px-2 py-1 rounded hover:bg-indigo-50" :class="isPresetActive('collected', p) ? 'bg-indigo-600 text-white hover:bg-indigo-600' : ''">{{ p.label }}</button>
                    </li>
                  </ul>
                </div>
                <div>
                  <p class="font-semibold mb-1">Mode</p>
                  <div class="flex flex-wrap gap-1">
                    <button v-for="m in ['range','month']" :key="m" type="button" @click="setDateMode('collected', m)" class="px-2 py-0.5 rounded border text-[11px]" :class="dateModes.collected===m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'">{{ m==='range' ? 'Range' : 'Month' }}</button>
                  </div>
                  <button v-if="taskDraft.collected_date" type="button" @click="taskDraft.collected_date=null; handleFilterChange()" class="mt-2 text-red-600 hover:underline">Clear</button>
                </div>
              </div>
            </template>
          </VueDatePicker>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1" for="task_report_status_dd">Task Reports Sent</label>
          <select id="task_report_status_dd" v-model="taskDraft.task_report_status" @change="handleFilterChange" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
            <option value="">All</option>
            <option value="sent">Sent</option>
            <option value="not_sent">Not Sent</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1" for="soil_report_status_dd">Soil Analysis Sent</label>
          <select id="soil_report_status_dd" v-model="taskDraft.soil_report_status" @change="handleFilterChange" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
            <option value="">All</option>
            <option value="sent">Sent</option>
            <option value="not_sent">Not Sent</option>
          </select>
        </div>
          </div>
        </div>
      </div>
    </FilterDrawer>
    </Teleport>

    <!-- Regular Tasks Table (Desktop) -->
    <div v-if="!issueMode && tasks.length > 0" class="hidden md:block">
      <div class="w-full">
        <table
          class="w-full table-auto divide-y divide-gray-400 border-b border-gray-400 border-collapse rounded-lg text-[13px] leading-tight"
        >
          <thead>
            <tr class="bg-gray-200 font-semibold text-gray-900">
              <td class="px-2 py-2.5 whitespace-nowrap cursor-pointer truncate"
                  :class="{ 'text-green-600': $root.task.sort === 'farmer' }"
                  @click="sorting('farmer')"
                  :title="$page.props.auth.user.role === 'farm_owner' ? 'Task Type' : 'Farmer'">
                <template v-if="$page.props.auth.user.role === 'farm_owner'">Task Type</template>
                <template v-else>Farmer↓</template>
              </td>
              <td class="px-2 py-2.5 whitespace-nowrap truncate">Task ID</td>
              <!-- Samples column -->
              <td class="px-2 py-2.5 whitespace-nowrap truncate text-center">Samples</td>
              <!-- Requester (hidden on small screens) -->
              <td class="px-2 py-2.5 whitespace-nowrap truncate hidden md:table-cell"
                  v-if="['admin','contractor','lab_user'].includes(role) || $page.props.auth.user.organisation?.type === 'lab'">
                Requester
              </td>
              <td class="px-2 py-2.5 whitespace-nowrap truncate" v-if="!issueMode">Status</td>
              <!-- Assigned To (hidden on small screens) -->
              <td class="px-2 py-2.5 whitespace-nowrap truncate hidden md:table-cell"
                  v-if="['admin','contractor','consultant','consultant_user','lab_user'].includes(role)">
                Assigned To
              </td>
              <!-- Created (hidden on small screens) -->
              <td class="px-2 py-2.5 whitespace-nowrap cursor-pointer truncate hidden md:table-cell"
                  :class="{ 'text-green-600': $root.task.sort === 'created' }"
                  v-if="!issueMode"
                  @click="sorting('created')"
                  title="Created">
                Created↓
              </td>
              <!-- Collected (hidden on small screens) -->
              <td class="px-2 py-2.5 whitespace-nowrap cursor-pointer truncate hidden md:table-cell"
                  :class="{ 'text-green-600': $root.task.sort === 'collected' }"
                  v-if="!issueMode"
                  @click="sorting('collected')"
                  title="Collected">
                Collected↓
              </td>
              <!-- Reports (hidden on small screens) -->
              <td class="px-2 py-2.5 whitespace-nowrap truncate text-center hidden md:table-cell" v-if="!issueMode" title="Reports Status">
                Reports
              </td>
              <td class="px-2 py-2.5 text-right">
                <div class="relative">
                  <span
                    role="button"
                    tabindex="0"
                    class="cursor-pointer"
                    :aria-expanded="showCsvMenu ? 'true' : 'false'"
                    :aria-disabled="csvCreating ? 'true' : 'false'"
                    @click="!csvCreating && (showCsvMenu = !showCsvMenu)"
                    @keydown.enter.prevent="!csvCreating && (showCsvMenu = !showCsvMenu)"
                    @keydown.space.prevent="!csvCreating && (showCsvMenu = !showCsvMenu)"
                  >
                    <template v-if="csvCreating">
                      <svg class="animate-spin h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                    </template>
                    <template v-else>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"
                        class="w-5 h-5 stroke-current hover:text-navy-dark"
                        :class="{ 'text-navy-dark': showCsvMenu, 'text-gray-500': !showCsvMenu }">
                        <g>
                          <circle cx="7" cy="2" r="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"></circle>
                          <circle cx="7" cy="7" r="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"></circle>
                          <circle cx="7" cy="12" r="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"></circle>
                        </g>
                      </svg>
                    </template>
                    <span class="sr-only">Open table actions menu</span>
                  </span>
                  <div
                    v-if="showCsvMenu"
                    class="absolute right-0 top-4 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-20"
                  >
                    <ul class="py-1 text-sm">
                      <li v-if="$root.task.sort">
                        <a href="#" class="block px-4 py-2 hover:bg-gray-100" @click.prevent="clearSort(); showCsvMenu=false">Clear Sort</a>
                      </li>
                      <li>
                        <a
                          href="#"
                          class="flex items-center justify-between px-4 py-2 hover:bg-gray-100 disabled:text-gray-400"
                          :class="csvCreating ? 'cursor-not-allowed opacity-70' : ''"
                          :aria-busy="csvCreating ? 'true' : 'false'"
                          @click.prevent="!csvCreating && generateCSV()"
                        >
                          <span>Export CSV</span>
                          <span v-if="csvCreating" class="ml-3 inline-flex items-center" aria-hidden="true">
                            <svg class="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                          </span>
                          <span class="sr-only" v-if="csvCreating">Preparing export</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </td>
            </tr>
          </thead>

          <tbody class="bg-white">
                      <tr
                        class="odd:bg-white even:bg-slate-100 text-gray-900 text-sm"
                        v-for="(task, index) in tasks"
                        :key="index"
                      >
                        <td class="px-2 py-2.5 whitespace-nowrap truncate"
                            :title="$page.props.auth.user.role === 'farm_owner' ? taskTypeLabel(task) : task.farmer_name">
                          {{ $page.props.auth.user.role === 'farm_owner' ? taskTypeLabel(task) : task.farmer_name }}
                        </td>
                        <td class="px-2 py-2.5 whitespace-nowrap font-mono truncate" :title="task.task_number">
                          {{ task.task_number }}
                        </td>
                        <!-- Samples count -->
                        <td class="px-2 py-2.5 whitespace-nowrap text-center" :title="task.samples_count + ' samples'">
                          {{ task.samples_count || 0 }}
                        </td>
                        <!-- Requester (hidden on small screens) -->
                        <td
                          class="px-2 py-2.5 whitespace-nowrap truncate hidden md:table-cell"
                          v-if="['admin', 'contractor', 'lab_user'].includes(role) || $page.props.auth.user.organisation?.type === 'lab'"
                          :title="task.advisor"
                        >
                          {{ task.advisor }}
                        </td>
                        <td class="px-2 py-2.5 whitespace-nowrap truncate" v-if="!issueMode" :title="task.status">
                          <div class="flex items-center space-x-2">
                            <span :class="statusPillClass(task)">{{ task.status }}</span>
                            <div
                              v-if="task.sample_path_issues > 0 || task.sample_zone_issues > 0"
                              class="w-4 h-4 text-center text-white text-[10px] leading-4 rounded-full"
                              :class="task.issues_class"
                            >
                              {{ task.issues_count }}
                            </div>
                          </div>
                        </td>

                        <!-- Assigned To (hidden on small screens) -->
                        <td
                          class="px-2 py-2.5 whitespace-nowrap truncate hidden md:table-cell"
                          v-if="[
                            'admin',
                            'contractor',
                            'consultant',
                            'consultant_user',
                            'lab_user',
                          ].includes(role)"
                          :title="task.assigned_to"
                        >
                          {{ task.assigned_to }}
                        </td>
                        <!-- Created (hidden on small screens) -->
                        <td class="px-2 py-2.5 whitespace-nowrap truncate hidden md:table-cell" :title="task.date">
                          {{ task.date }}
                        </td>
                        <!-- Collected (hidden on small screens) -->
                        <td class="px-2 py-2.5 whitespace-nowrap truncate hidden md:table-cell" v-if="!issueMode" :title="task.collected">
                          {{ task.collected }}
                        </td>
                        <!-- Reports (hidden on small screens) -->
                        <td class="px-2 py-2.5 text-center hidden md:table-cell" v-if="!issueMode">
                          <!-- Reports status compact indicator or loading skeleton -->
                          <div v-if="hasReports(task)"
                            class="flex items-stretch rounded cursor-help overflow-hidden mx-auto w-6 h-4 border border-gray-300"
                            :title="getReportsTooltip(task)"
                          >
                            <div class="flex-1" :class="getTaskReportClass(task)"></div>
                            <div class="w-px bg-white/80 dark:bg-white/60"></div>
                            <div class="flex-1" :class="getSoilAnalysisClass(task)"></div>
                          </div>
                          <span v-else class="text-gray-400 text-xs">–</span>
                        </td>

                        <td class="px-2 py-2.5 text-right">
                          <index-menu-item :links="indexLinks(task)" />
                        </td>
                      </tr>
                    </tbody>
        </table>
      </div>
    </div>

    <!-- Issues Mode - Mobile and Desktop -->
    <div v-if="issueMode && activeTab === 'tasks'">
      <!-- Mobile cards for Issues -->
      <div v-if="tasksWithIssues && tasksWithIssues.data && tasksWithIssues.data.length > 0" class="space-y-3 md:hidden px-1">
        <div
          v-for="(task, index) in tasksWithIssues.data"
          :key="'issue-mcard-'+index"
          class="bg-white rounded-lg border border-gray-200 p-3 flex flex-col gap-2 shadow-sm"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0">
                <h3 class="text-sm font-semibold text-navy truncate" :title="task.farmer_name">{{ task.farmer_name }}</h3>
                <span class="text-[11px] font-mono text-gray-500 truncate" :title="task.task_number">#{{ task.task_number }}</span>
              </div>
              <div class="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
                <span :class="[...statusPillClass(task), 'px-2 py-0.5 text-[11px]']">{{ task.status }}</span>
                <div v-if="task.logsheet_issues > 0 || task.full_logsheet_issues > 0" class="flex items-center text-red-600 gap-0.5">
                  <LogsheetIssue class="w-4 h-4" /> <span>Logsheet</span>
                </div>
                <div v-if="task.sample_path_issues > 0" class="flex items-center text-yellow-600 gap-0.5">
                  <SampleIssueIcon class="w-4 h-4" /> <span>Sample</span>
                </div>
                <div v-if="task.sample_zone_issues > 0" class="flex items-center text-red-600 gap-0.5">
                  <MatchingIssueIcon class="w-4 h-4" /> <span>Matching</span>
                </div>
              </div>
            </div>
            <div class="shrink-0 flex items-center space-x-2">
              <index-menu-item :links="indexLinks(task)" />
              <a v-if="['admin','consultant'].includes(role)" href="#" @click.prevent="closeAllTaskIssues(task)" class="text-[11px] text-red-600 font-medium">Close</a>
            </div>
          </div>
          <div class="text-[12px] leading-tight text-gray-700" v-if="task.issues && task.issues.length">
            <span>
              {{ task.showFullDescription ? displayIssuesDescriptions(task.issues).join() : displayIssuesDescriptions(task.issues)[0] }}
              <a
                role="button"
                tabindex="0"
                class="text-[11px] text-blue-700 underline ml-1"
                @click="task.showFullDescription = !task.showFullDescription"
                @keydown.enter.prevent="task.showFullDescription = !task.showFullDescription"
              >{{ task.showFullDescription ? 'Less' : 'More' }}</a>
            </span>
          </div>
        </div>
      </div>

      <!-- Desktop table for Issues -->
      <div v-if="tasksWithIssues && tasksWithIssues.data && tasksWithIssues.data.length > 0" class="hidden md:block">
        <div class="w-full px-1">
          <div class="w-full overflow-x-auto shadow border-b border-gray-200">
            <table class="table-auto min-w-full divide-y divide-gray-400 border-b border-gray-400 border-collapse rounded-lg text-sm">
            <thead>
              <tr class="bg-gray-200 font-semibold text-gray-900">
                <td class="min-w-40 px-2 py-2.5">Farmer</td>
                <td class="min-w-40 px-2 py-2.5">Task</td>
                <td class="px-2 py-2.5">Status</td>
                <td class="px-2 py-2.5"><LogsheetIssue /></td>
                <td class="px-2 py-2.5"><SampleIssueIcon /></td>
                <td class="px-2 py-2.5"><MatchingIssueIcon /></td>
                <td class="px-2 py-2.5">Description</td>
                <td class="px-2 py-2.5 text-center" v-if="['admin', 'consultant'].includes(role)"></td>
                <td class="px-2 py-2.5 text-center"></td>
              </tr>
            </thead>
            <tbody class="bg-white overflow-y-auto">
              <tr
                class="odd:bg-white even:bg-slate-100 text-gray-900 text-sm"
                v-for="(task, index) in tasksWithIssues.data"
                :key="index"
              >
                <td class="px-2 py-2.5">{{ task.farmer_name }}</td>
                <td class="px-2 py-2.5">{{ task.task_number }}</td>
                <td class="px-2 py-2.5">
                  <div class="flex"><div>{{ task.status }}</div></div>
                </td>
                <td class="px-2 py-2.5 w-5 items-center justify-center place-items-center">
                  <!-- ISEC organisation rules -->
                  <div
                    v-if="task.organisation_name == 'ISEC'"
                    class="w-4 h-4 mx-auto text-center text-white text-xs rounded-full"
                    :class="{
                      'bg-red-600': task.logsheet_issues > 0 && task.partial_logsheet_issues > 0,
                      'bg-yellow-600': task.partial_logsheet_issues < 1 && task.full_logsheet_issues > 0,
                      'bg-green-600': task.full_logsheet_issues < 1 || (task.task_status == 'collected' && task.logsheet_issues < 1)
                    }"
                  >
                    <inertia-link :href="route('app.task.view', { task: task.id, tab: 'actions' })"><span>&nbsp;</span></inertia-link>
                  </div>
                  <!-- Non-ISEC -->
                  <div
                    v-else
                    class="w-4 h-4 mx-auto text-center text-white text-xs rounded-full"
                    :class="task.logsheet_issues > 0 ? 'bg-red-600' : 'bg-green-600'"
                  >
                    <inertia-link :href="route('app.task.view', { task: task.id, tab: 'actions' })"><span>&nbsp;</span></inertia-link>
                  </div>
                </td>
                <td class="px-2 py-2.5 w-5 justify-center place-items-center">
                  <div
                    class="w-4 h-4 mx-auto text-center text-white text-xs rounded-full"
                    :class="task.sample_path_issues > 0 ? 'bg-yellow-600' : 'bg-green-600'"
                  >
                    <inertia-link :href="route('app.task.view', { task: task.id, tab: 'records' })">
                      <span v-if="task.sample_path_issues > 0">{{ task.sample_path_issues }}</span>
                      <span v-else>&nbsp;</span>
                    </inertia-link>
                  </div>
                </td>
                <td class="px-1 w-5 items-center justify-center place-items-center">
                  <div
                    class="w-4 mx-auto h-4 text-center text-white text-xs rounded-full"
                    :class="task.sample_zone_issues > 0 ? 'bg-red-600' : 'bg-green-600'"
                  >
                    <inertia-link :href="route('app.task.view', { task: task.id, tab: 'records' })">
                      <span v-if="task.sample_zone_issues > 0">{{ task.sample_zone_issues }}</span>
                      <span v-else>&nbsp;</span>
                    </inertia-link>
                  </div>
                </td>
                <td class="px-2 py-2.5 w-min-[112px]">
                  <span v-if="task.issues.length > 0">
                    {{ task.showFullDescription ? displayIssuesDescriptions(task.issues).join() : displayIssuesDescriptions(task.issues)[0] }}
                    <a
                      href="#"
                      v-if="displayIssuesDescriptions(task.issues).length > 2"
                      @click.prevent="task.showFullDescription = !task.showFullDescription"
                      class="text-xs text-blue-800 underline"
                    >{{ task.showFullDescription ? 'See Less' : '...  See More' }}</a>
                  </span>
                </td>
                <td class="w-[80px] text-center" v-if="['admin', 'consultant'].includes(role)">
                  <a href="#" @click.prevent="closeAllTaskIssues(task)" class="text-xs text-red-600 w-[80px]">Close</a>
                </td>
                <td class="px-2 py-2.5 text-center">
                  <div class="flex justify-center">
                    <index-menu-item :links="indexLinks(task)" />
                  </div>
                </td>
              </tr>
            </tbody>
            </table>
          </div>
          <div class="flex items-center justify-center hidden md:flex">
            <json-pagination
              class="mt-8 mb-4"
              :links="tasksWithIssues?.meta?.pagination"
              :link="tasksIssuesLink"
              @load-json="handlePaginatingData"
            />
          </div>
        </div>
      </div>
      <div v-else-if="tasksWithIssues && !loading" class="bg-white mt-8 px-6 py-4">
        <strong v-if="tasksWithIssues.debug_empty">No tasks with open issues.</strong>
        <strong v-else>No issues found.</strong>
      </div>
      <div v-else-if="loading" class="mt-8 text-center text-sm text-gray-500">Loading issues...</div>
      <div v-else-if="tasksWithIssues === null" class="bg-white mt-8 px-6 py-4">
        <strong>Click the notifications button to load issues.</strong>
      </div>
    </div>

    <!-- <span ref="loadMoreIntersect"></span> -->

    <!-- No tasks message for mobile -->
    <div v-if="tasks.length === 0 && activeTab === 'tasks'" class="md:hidden bg-white mt-8 px-6 py-4">
      <strong>No tasks found.</strong>
    </div>
    
    <!-- No tasks message for desktop -->
    <div v-if="tasks.length === 0" class="hidden md:block bg-white mt-8 px-6 py-4">
      <strong>No tasks found.</strong>
    </div>

    <!-- Add pagination for regular tasks view - Mobile -->
    <div v-if="!issueMode && tasks.length > 0 && activeTab === 'tasks'" class="md:hidden flex items-center justify-center">
      <pagination class="mt-8 mb-4" :links="links" :on-page-change="handlePageChange" />
    </div>
    
    <!-- Add pagination for regular tasks view - Desktop -->
    <div v-if="!issueMode && tasks.length > 0" class="hidden md:flex items-center justify-center">
      <pagination class="mt-8 mb-4" :links="links" :on-page-change="handlePageChange" />
    </div>

    <!-- Add pagination for issues mode - Mobile -->
    <div v-if="issueMode && tasksWithIssues && tasksWithIssues.data && tasksWithIssues.data.length > 0 && activeTab === 'tasks'" class="md:hidden flex items-center justify-center">
      <json-pagination 
        class="mt-8 mb-4" 
        :links="tasksWithIssues.links || tasksWithIssues.meta?.links"
        @paginating="handlePaginatingData"
      />
    </div>
  </div>
  </div>
</template>

<script>
import AppLayout from "@/Layouts/App";
import Pagination from "@/Components/Pagination";
import JsonPagination from "@/Components/JsonPagination";
import { useToast } from "vue-toastification";
import IndexMenuItem from "@/Components/IndexMenuItem";
import LogsheetIssue from "@/Components/Icons/LogsheetIssue.vue";
import MatchingIssueIcon from "@/Components/Icons/MatchingIssueIcon.vue";
import SampleIssueIcon from "@/Components/Icons/SampleIssueIcon.vue";
import { mdiClipboardListOutline, mdiUpload } from "@mdi/js";
import SearchField from "@/Components/SearchField.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import FilterDrawer from "@/Components/FilterDrawer.vue";

import _ from "lodash";
import axios from "axios";
import { countActiveFilters } from '@/Composables/useActiveFilters';
import dateFnsFormat from "date-fns/format";

// Filters go to the server as local calendar days (YYYY-MM-DD). Not toISOString(): it
// converts to UTC first, so in Irish Summer Time local midnight becomes the previous day.
const toYMD = (d) => (d instanceof Date ? dateFnsFormat(d, "yyyy-MM-dd") : d);

// Month-picker value: { month, year } (range mode gives [Date, Date])
const isMonthObj = (v) => v?.month !== undefined && v?.year !== undefined;

// A month-picker value or any date in a month -> [first day, last day]
const expandMonth = (dt) => {
  if (dt instanceof Date) {
    return [new Date(dt.getFullYear(), dt.getMonth(), 1), new Date(dt.getFullYear(), dt.getMonth() + 1, 0)];
  }
  if (isMonthObj(dt)) {
    return [new Date(dt.year, dt.month, 1), new Date(dt.year, dt.month + 1, 0)];
  }
  return null;
};

export default {
  name: "tasksManager",
  layout: [AppLayout],
  components: {
    LogsheetIssue,
    SampleIssueIcon,
    MatchingIssueIcon,
    AppLayout,
    Pagination,
    JsonPagination,
    IndexMenuItem,
    SearchField,
    VueDatePicker,
    FilterDrawer,
  },

  props: {
    role: String,
    search: String,
    results: Object,
    statuses: Object,
    totalIssues: Number,
    paginate: String,
    smap_sample_paths: Array,
    consultants: Array,
    advisors: { type: [Array, Object], default: () => [] },
    dashboardStats: { type: Object, default: () => null },
    // Newly added filter props from backend to persist selections
    status_filter: String,
    scope_filter: String,
    advisor_filter: String,
    consultant_filter: String,
    contractor_filter: String,
    active_date_start: String,
    active_date_end: String,
    task_report_status_filter: String,
    soil_report_status_filter: String,
    created_date_start: String,
    created_date_end: String,
    collected_date_start: String,
    collected_date_end: String,
    // Search field props
    farmer_name_search: String,
    task_number_search: String,
    sample_id_search: String,
  },

  data() {
    return {
      tasks: this.results["original"]["data"],
      // pages: this.results['original']['meta']['pagination']['total_pages'],
      links: this.results["original"]["meta"]["pagination"],
      // Local copy of dashboard stats that syncs with axios responses
      localDashboardStats: this.dashboardStats || {},
      contractors: [], // holds Assigned To list populated via getContractor()
      issueMode: false, // toggles inbox/issues view
      tasksWithIssues: null, // stores tasks with issues response
      filteredIssuesCount: null, // tracks the filtered issues count for notification badge
      loading: false,
      activeTab: 'tasks', // track mobile tab state (dashboard/tasks)
      showFilters: false,

      taskDraft: {},
      // table actions / export state
      showCsvMenu: false,
      csvCreating: false,
      csvAvailable: false,
      csvRequested: false,
  // (Legacy) Per-task async report status removed; now using aggregated fields in payload
      dateModes: { active: 'range', created: 'range', collected: 'range' },
      // Custom advisor dropdown state
      advisorDropdownOpen: false,
      advisorSearch: '',
      // Custom contractor dropdown state
      contractorDropdownOpen: false,
      contractorSearch: '',
      // Custom status dropdown state
      statusDropdownOpen: false,
      statusSearch: '',
      // Provide defaults to silence template warnings if not yet populated
      sample_path_issues: 0,
      tasksIssuesLink: null,
      icons: {
        tasks: mdiClipboardListOutline,
        csvImport: mdiUpload,
      },
      // Search fields data
      searchFields: {
        farmer_name: this.farmer_name_search || '',
        task_number: this.task_number_search || '',
        sample_id: this.sample_id_search || '',
      },
      searchInProgress: false,
    };
  },

  watch: {
    'taskDraft.created_date'() {
      // Track when user manually changes created date (not during initialization or cloning)
      if (this._isMounted && !this._isCloningDates) {
        // Check if the draft date is actually different from the root date
        const rootDate = this.$root.task?.created_date;
        const draftDate = this.taskDraft.created_date;
        
        // Helper to compare date arrays
        const datesEqual = (date1, date2) => {
          if (!date1 && !date2) return true;
          if (!date1 || !date2) return false;
          if (Array.isArray(date1) && Array.isArray(date2)) {
            return date1.length === date2.length && 
                   date1.every((d, i) => d && date2[i] && d.getTime() === date2[i].getTime());
          }
          return false;
        };
        
        // Only refilter if the dates are actually different
        if (!datesEqual(draftDate, rootDate)) {
          // Trigger instant filtering
          this.handleFilterChange();
        }
      }
    },
    'taskDraft.date'() {
      // Trigger instant filtering when active date changes
      if (this._isMounted) {
        this.handleFilterChange();
      }
    },
    'taskDraft.collected_date'() {
      // Trigger instant filtering when collected date changes
      if (this._isMounted) {
        this.handleFilterChange();
      }
    },
    // Add watcher for tasks to emit locations when filtered tasks change
    tasks: {
      handler() {
        // Emit task locations when tasks change (due to filtering)
        if (this._isMounted && !this._isDestroying) {
          this.$nextTick(() => {
            // Double-check component state before emitting
            if (!this._isDestroying && this._isMounted) {
              this.emitTaskLocations();
            }
          });
        }
      },
      immediate: false
    },
    taskDraft: {
      handler(newVal) {
      },
      deep: true
    },
    dashboardStats: {
      handler(newVal, oldVal) {
        console.log('===== WATCHER: dashboardStats prop changed =====');
        console.log('New value:', newVal);
        console.log('Old value:', oldVal);
        if (newVal) {
          console.log('New dashboardStats keys:', Object.keys(newVal));
          console.log('New dashboardStats values:', JSON.parse(JSON.stringify(newVal)));
        }
        console.trace('Stack trace:');
      },
      deep: true
    }
  },

  mounted() {
        // If any filters are present in taskDraft or $root.task, apply them immediately so data matches UI
        const hasActiveFilters = () => {
          // Check for any non-empty filter fields
          const filterKeys = ['status','filter','advisor','user_filter','contractor','task_report_status','soil_report_status'];
          const hasFilterValues = filterKeys.some(k => {
            const draftVal = this.taskDraft && this.taskDraft[k];
            const rootVal = this.$root.task && this.$root.task[k];
            
            // Handle array fields (advisor, status)
            if (k === 'advisor' || k === 'status') {
              return (Array.isArray(draftVal) && draftVal.length > 0) || 
                     (Array.isArray(rootVal) && rootVal.length > 0) ||
                     (typeof draftVal === 'string' && draftVal !== '') ||
                     (typeof rootVal === 'string' && rootVal !== '');
            }
            
            // Handle string fields
            return (draftVal && draftVal !== '') || (rootVal && rootVal !== '');
          });
          
          // Check for any non-empty search fields
          const hasSearchValues = this.searchFields && Object.values(this.searchFields).some(val => val && val.trim() !== '');
          
          return hasFilterValues || hasSearchValues;
        };
        if (hasActiveFilters()) {
          // Check if we have search values vs filter values to call the right method
          const hasSearchValues = this.searchFields && Object.values(this.searchFields).some(val => val && val.trim() !== '');
          if (hasSearchValues) {
            // Use performSearchOnly for search fields (includes both search and filter logic)
            this.performSearchOnly();
          } else {
            // Use handleFilterChange for regular filters
            this.handleFilterChange();
          }
        }
    // Hide farmer pins from the farmers page when viewing tasks page
    this.$emitter.emit('hideFarmerPins');
    
    this.toast = useToast();
    this.getContractor();
  // Report statuses now included in initial payload (no async fetch)
    
    // Ensure root task object exists
    if (!this.$root.task) {
      this.$root.task = {};
    }
    
    // Always close filter panel on mount (do not persist across navigation)
    this.showFilters = false;
    
    // Initialize search fields from root state if they exist
    if (this.$root.searchFields) {
      this.searchFields = { ...this.$root.searchFields };
    }

    // Apply persisted filter values from props FIRST so they show in dropdowns after refresh
    const mapping = {
      status: this.status_filter,
      filter: this.scope_filter,
      advisor: this.advisor_filter,
      user_filter: this.consultant_filter,
      contractor: this.contractor_filter,
      task_report_status: this.task_report_status_filter,
      soil_report_status: this.soil_report_status_filter,
    };
    Object.keys(mapping).forEach(k => {
      const val = mapping[k];
      if (val !== undefined && val !== null && val !== '') {
        if (k === 'advisor' || k === 'status') {
          // Handle advisor and status fields as arrays
          this.$root.task[k] = Array.isArray(val) ? val : [val];
        } else {
          this.$root.task[k] = val;
        }
      }
    });

    // Date range persistence: if active_date_start/end provided and not already set, hydrate date picker model
    if (!this.$root.task.date && this.active_date_start && this.active_date_end) {
      try {
        const startParts = this.active_date_start.split('-');
        const endParts = this.active_date_end.split('-');
        const startDate = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
        const endDate = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
        this.$root.task.date = [startDate, endDate];
      } catch (e) {
        // silent fail if parsing error
      }
    }

    // Created date range persistence
    if (!this.$root.task.created_date && this.created_date_start && this.created_date_end) {
      try {
        const startParts = this.created_date_start.split('-');
        const endParts = this.created_date_end.split('-');
        const startDate = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
        const endDate = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
        this.$root.task.created_date = [startDate, endDate];
      } catch (e) {}
    }

    // Now set defaults ONLY for missing keys (preserving applied values)
    ['status','filter','user_filter','contractor','task_report_status','soil_report_status'].forEach(key => {
      if (typeof this.$root.task[key] === 'undefined' || this.$root.task[key] === null) {
        this.$root.task[key] = '';
      }
    });
    
    // Special handling for advisor field to ensure it's always an array
    if (typeof this.$root.task.advisor === 'undefined' || this.$root.task.advisor === null) {
      this.$root.task.advisor = [];
    } else if (typeof this.$root.task.advisor === 'string' && this.$root.task.advisor !== '') {
      // Convert single string value to array for backward compatibility
      this.$root.task.advisor = [this.$root.task.advisor];
    } else if (!Array.isArray(this.$root.task.advisor)) {
      this.$root.task.advisor = [];
    }
  // Only initialise created_date if truly undefined (don't overwrite hydrated range)
  if (typeof this.$root.task.created_date === 'undefined') this.$root.task.created_date = null;
    // Collected date range persistence
    if (!this.$root.task.collected_date && this.collected_date_start && this.collected_date_end) {
      try {
        const startParts = this.collected_date_start.split('-');
        const endParts = this.collected_date_end.split('-');
        const startDate = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
        const endDate = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
        this.$root.task.collected_date = [startDate, endDate];
      } catch (e) {}
    }
  // Only initialise collected_date if truly undefined (don't overwrite hydrated range)
  if (typeof this.$root.task.collected_date === 'undefined') this.$root.task.collected_date = null;

    // Search field persistence
    if (typeof this.$root.task.search === 'undefined' || this.$root.task.search === null) {
      this.$root.task.search = this.search || '';
    }

    // Infer initial date modes (range vs month) from stored values so drawer shows correct state
    if (isMonthObj(this.$root.task.date)) this.dateModes.active = 'month';
    if (isMonthObj(this.$root.task.created_date)) this.dateModes.created = 'month';
    if (isMonthObj(this.$root.task.collected_date)) this.dateModes.collected = 'month';
    
    // Filters with no URL params come from $root.task, not from the server render
    const currentUrl = new URL(window.location);
    const hasAnyFilterParams = currentUrl.searchParams.has('created_date_start') || 
                               currentUrl.searchParams.has('created_date_end') ||
                               currentUrl.searchParams.has('status') ||
                               currentUrl.searchParams.has('search') ||
                               Array.from(currentUrl.searchParams.keys()).some(key => key.includes('filter'));

    // Set the mounted flag to track initialization completion
    this._isMounted = true;
    
    // Initialize mobile tab state - default to tasks tab
    if (!this.activeTab) {
      this.activeTab = 'tasks';
    }

    // Without URL filter params the server rendered an unfiltered list, but $root.task
    // keeps the filters from earlier visits. Refetch so the
    // list and pagination match the filters that later page clicks will send.
    if (!hasAnyFilterParams) {
      this.$root.task.page = 1;
      this.$nextTick(() => {
        this.applyFiltersWithoutClosing();
      });
    }
    
    // Set the mounted flag to track initialization completion
    this._isMounted = true;
    
    // Show task pins layer when entering the task page
    this.$emitter.emit('showTaskPins');
    
    // Emit initial task locations after component is fully mounted
    this.$nextTick(() => {
      // Check component state before emitting
      if (!this._isDestroying && this._isMounted) {
        this.emitTaskLocations();
      }
    });
  },

  methods: {
    // Always persist filter panel state to $root when user opens/closes it
    openFilters() {
      this.showFilters = true;
      this.$root.showFilters = true;
    },
    closeFilters() {
      this.showFilters = false;
      this.$root.showFilters = false;
    },

    // Handle tab switching
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    // Emit task locations to map - gets ALL tasks matching current filters (not just current page)
    async emitTaskLocations() {
      try {
        // Build the same filter parameters that are used for the main task query
        const params = { ...this.$root.task };
        
        // Remove pagination-specific params since we want all results
        delete params.page;
        
        // Handle consultant assignment logic (same as filtering method)
        if (this.$root.task.sort != "my_farmers") {
          params.consultant = "";
        } else {
          params.consultant = this.$page.props.auth.user.id;
        }
        
        // Process date filters the same way as filtering() and applyTaskFilters()
        delete params.date;
        delete params.created_date;
        delete params.collected_date;

        if (this.$root.task.date) {
          if (this.dateModes.active === 'month') {
            const expanded = expandMonth(this.$root.task.date);
            if (expanded) {
              params.active_date_start = toYMD(expanded[0]);
              params.active_date_end = toYMD(expanded[1]);
            }
          } else if (this.dateModes.active === 'range' && Array.isArray(this.$root.task.date)) {
            params.active_date_start = toYMD(this.$root.task.date[0]);
            params.active_date_end = toYMD(this.$root.task.date[1]);
          }
        }
        
        if (this.$root.task.created_date) {
          if (this.dateModes.created === 'month') {
            const expanded = expandMonth(this.$root.task.created_date);
            if (expanded) {
              params.created_date_start = toYMD(expanded[0]);
              params.created_date_end = toYMD(expanded[1]);
            }
          } else if (this.dateModes.created === 'range' && Array.isArray(this.$root.task.created_date)) {
            params.created_date_start = toYMD(this.$root.task.created_date[0]);
            params.created_date_end = toYMD(this.$root.task.created_date[1]);
          }
        }
        
        if (this.$root.task.collected_date) {
          if (this.dateModes.collected === 'month') {
            const expanded = expandMonth(this.$root.task.collected_date);
            if (expanded) {
              params.collected_date_start = toYMD(expanded[0]);
              params.collected_date_end = toYMD(expanded[1]);
            }
          } else if (this.dateModes.collected === 'range' && Array.isArray(this.$root.task.collected_date)) {
            params.collected_date_start = toYMD(this.$root.task.collected_date[0]);
            params.collected_date_end = toYMD(this.$root.task.collected_date[1]);
          }
        }
        
        // Call the new map-locations endpoint
        const response = await axios.get(route("app.task.map-locations"), { params });
        
        // Check if component is still mounted and not being destroyed before emitting
        if (this._isDestroying || !this._isMounted) {
          return;
        }
        
        if (response.data && response.data.tasks && response.data.tasks.length > 0) {
          this.$emitter.emit('showTaskLocations', {
            tasks: response.data.tasks
          });
        } else {
          // Clear task locations if no tasks match current filters
          this.$emitter.emit('showTaskLocations', {
            tasks: []
          });
        }
      } catch (error) {
        console.error('Error loading task locations for map:', error);
        
        // Check if component is still mounted and not being destroyed before fallback
        if (this._isDestroying || !this._isMounted) {
          return;
        }
        
        // Fallback to current page tasks if API call fails
        if (this.tasks && this.tasks.length > 0) {
          const tasksWithLocations = this.tasks.filter(task => task.farm_location);
          if (tasksWithLocations.length > 0) {
            this.$emitter.emit('showTaskLocations', {
              tasks: tasksWithLocations
            });
          }
        }
      }
    },
    
    // Generic formatting helper: accepts value and mode key (active|created|collected)
    _formatDatePicker(value, modeKey){
      if (!value) return '';
      const mode = this.dateModes[modeKey];
      
      // Check if the value matches any preset first (for range mode)
      if (mode === 'range' && Array.isArray(value) && value.length === 2) {
        const preset = this.datePresets.find(p => {
          if (!p.range || !Array.isArray(p.range)) return false;
          const [start, end] = value;
          const [presetStart, presetEnd] = p.range;
          return start instanceof Date && end instanceof Date && presetStart instanceof Date && presetEnd instanceof Date &&
                 start.getTime() === presetStart.getTime() && end.getTime() === presetEnd.getTime();
        });
        if (preset) {
          return preset.label;
        }
      }
      
      const pad = (n) => String(n).padStart(2,'0');
      const toStr = (d) => (d instanceof Date) ? `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}` : '';
      // Month mode: value may be our legacy month object OR a Date instance
      if (mode === 'month') {
        if (value instanceof Date) {
          return value.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        }
        if (isMonthObj(value)) {
          const labelDate = new Date(value.year, value.month, 1);
          return labelDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        }
      }
      // Range mode array (fallback to date format if no preset match)
      if (Array.isArray(value)) {
        const parts = value.filter(Boolean).map(toStr);
        return parts.join(' - ');
      }
      // Fallback (single date in range mode) => treat as day
      return toStr(value);
    },
    formatActive(value){ return this._formatDatePicker(value, 'active'); },
    formatCreated(value){ return this._formatDatePicker(value, 'created'); },
    formatCollected(value){ return this._formatDatePicker(value, 'collected'); },
    // Custom advisor dropdown methods
    selectAdvisor(option) {
      this.taskDraft.advisor = Array.isArray(this.taskDraft.advisor) ? [option.value] : option.value;
      this.advisorSearch = '';
      this.advisorDropdownOpen = false;
      this.handleFilterChange();
    },
    // Multi-select methods for advisor
    toggleAdvisorSelection(option) {
      if (!Array.isArray(this.taskDraft.advisor)) {
        this.taskDraft.advisor = [];
      }
      
      const index = this.taskDraft.advisor.indexOf(option.value);
      if (index > -1) {
        // Remove if already selected
        this.taskDraft.advisor.splice(index, 1);
      } else {
        // Add if not selected
        this.taskDraft.advisor.push(option.value);
      }
      
      this.handleFilterChange();
    },
    isAdvisorSelected(value) {
      if (!Array.isArray(this.taskDraft.advisor)) {
        return this.taskDraft.advisor === value;
      }
      return this.taskDraft.advisor.includes(value);
    },
    clearAdvisorSelection() {
      this.taskDraft.advisor = [];
      this.advisorSearch = '';
      this.advisorDropdownOpen = false;
      this.handleFilterChange();
    },
    // Custom contractor dropdown methods
    selectContractor(option) {
      this.taskDraft.contractor = option.value;
      this.contractorSearch = '';
      this.contractorDropdownOpen = false;
      this.handleFilterChange();
    },
    clearContractorSelection() {
      this.taskDraft.contractor = '';
      this.contractorSearch = '';
      this.contractorDropdownOpen = false;
      this.handleFilterChange();
    },
    // Status multi-select methods
    toggleStatusSelection(option) {
      if (!Array.isArray(this.taskDraft.status)) {
        this.taskDraft.status = [];
      }
      
      const index = this.taskDraft.status.indexOf(option.value);
      if (index > -1) {
        // Remove if already selected
        this.taskDraft.status.splice(index, 1);
      } else {
        // Add if not selected
        this.taskDraft.status.push(option.value);
      }
      
      this.handleFilterChange();
    },
    isStatusSelected(value) {
      if (!Array.isArray(this.taskDraft.status)) {
        return this.taskDraft.status === value;
      }
      return this.taskDraft.status.includes(value);
    },
    clearStatusSelection() {
      this.taskDraft.status = [];
      this.statusSearch = '';
      this.statusDropdownOpen = false;
      this.handleFilterChange();
    },
    // Status multi-select methods
    toggleStatusSelection(option) {
      if (!Array.isArray(this.taskDraft.status)) {
        this.taskDraft.status = [];
      }
      
      const index = this.taskDraft.status.indexOf(option.value);
      if (index > -1) {
        // Remove if already selected
        this.taskDraft.status.splice(index, 1);
      } else {
        // Add if not selected
        this.taskDraft.status.push(option.value);
      }
      
      this.handleFilterChange();
    },
    isStatusSelected(value) {
      if (!Array.isArray(this.taskDraft.status)) {
        return this.taskDraft.status === value;
      }
      return this.taskDraft.status.includes(value);
    },
    clearStatusSelection() {
      this.taskDraft.status = [];
      this.statusSearch = '';
      this.statusDropdownOpen = false;
      this.handleFilterChange();
    },
    openFilters(){
      // clone current root state into draft
      const src = this.$root.task || {};
      const cloneDate = (val) => {
        if (!val) return null;
        if (Array.isArray(val)) return val.map(d => d ? new Date(d) : d);
        if (val instanceof Date) return new Date(val);
        if (isMonthObj(val)) return { ...val };
        return null;
      };

      // Flag to prevent watcher from triggering during cloning
      this._isCloningDates = true

      this.taskDraft = {
        status: Array.isArray(src.status) ? src.status : (src.status ? [src.status] : []),
        filter: src.filter || '',
        advisor: Array.isArray(src.advisor) ? src.advisor : (src.advisor ? [src.advisor] : []),
        user_filter: src.user_filter || '',
        contractor: src.contractor || '',
        task_report_status: src.task_report_status || '',
        soil_report_status: src.soil_report_status || '',
        date: cloneDate(src.date),
        created_date: cloneDate(src.created_date),
        collected_date: cloneDate(src.collected_date),
      };

      // Clear flag after cloning is complete
      this._isCloningDates = false

      // Sync search fields with current state
      this.searchFields = {
        farmer_name: src.farmer_name || '',
        task_number: src.task_number || '',
        sample_id: src.sample_id || '',
      };
      // Sync mode toggles with existing stored value shapes so the picker renders correctly
      this.dateModes.active = isMonthObj(this.taskDraft.date) ? 'month' : 'range';
      this.dateModes.created = isMonthObj(this.taskDraft.created_date) ? 'month' : 'range';
      this.dateModes.collected = isMonthObj(this.taskDraft.collected_date) ? 'month' : 'range';
      this.showFilters = true;
      this.$root.showFilters = true;
    },
    closeFilters(){
      this.showFilters = false;
      this.$root.showFilters = false;
      this.$nextTick(()=>{ this.$refs.taskFiltersButton && this.$refs.taskFiltersButton.focus(); });
    },
    applyTaskFilters(){
      // push draft values back to root + trigger filtering
      Object.assign(this.$root.task, this.taskDraft);
      // ensure page reset
      this.$root.task.page = 1;
      // Build payload similar to filtering() but using updated root
      const payload = { ...this.$root.task };
      delete payload.date;
      delete payload.created_date;
      delete payload.collected_date;
      if (this.$root.task.date) {
        if (this.dateModes.active === 'month') {
          const rng = expandMonth(this.$root.task.date);
          if (rng) {
            payload.active_date_start = toYMD(rng[0]);
            payload.active_date_end = toYMD(rng[1]);
          }
        } else if (Array.isArray(this.$root.task.date)) {
          payload.active_date_start = toYMD(this.$root.task.date[0]);
          payload.active_date_end = toYMD(this.$root.task.date[1]);
        }
      } else {
        delete this.$root.task.active_date_start;
        delete this.$root.task.active_date_end;
      }
      if (this.$root.task.created_date) {
        if (this.dateModes.created === 'month') {
          const rng = expandMonth(this.$root.task.created_date);
          if (rng) {
            payload.created_date_start = toYMD(rng[0]);
            payload.created_date_end = toYMD(rng[1]);
          }
        } else if (Array.isArray(this.$root.task.created_date)) {
          payload.created_date_start = toYMD(this.$root.task.created_date[0]);
          payload.created_date_end = toYMD(this.$root.task.created_date[1]);
        }
      } else {
        // When created_date is null (All Time selected), don't send any date parameters
        delete this.$root.task.created_date_start;
        delete this.$root.task.created_date_end;
      }
      if (this.$root.task.collected_date) {
        if (this.dateModes.collected === 'month') {
          const rng = expandMonth(this.$root.task.collected_date);
            if (rng) {
              payload.collected_date_start = toYMD(rng[0]);
              payload.collected_date_end = toYMD(rng[1]);
            }
        } else if (Array.isArray(this.$root.task.collected_date)) {
          payload.collected_date_start = toYMD(this.$root.task.collected_date[0]);
          payload.collected_date_end = toYMD(this.$root.task.collected_date[1]);
        }
      } else {
        delete this.$root.task.collected_date_start;
        delete this.$root.task.collected_date_end;
      }
      this.showFilters = false;
      
      // If we're in issue mode (inbox), refresh the issues view with new filters
      if (this.issueMode) {
        this.loadIssuesTasks();
      } else {
        // Reset filtered issues count when not in issue mode - it will be recalculated on page reload
        this.filteredIssuesCount = null;
      }
      
      this.$inertia.get(route("app.task.index"), payload);
    },
    clearDrawerFilters(){
      // Clear all filters from both taskDraft and $root.task
      ['status','filter','advisor','user_filter','contractor','task_report_status','soil_report_status'].forEach(k => { 
        this.taskDraft[k] = '';
        if (this.$root.task[k] !== undefined) {
          this.$root.task[k] = '';
        }
      });
      
      // Clear date filters from both taskDraft and $root.task
      this.taskDraft.date = null;
      this.taskDraft.created_date = null;
      this.taskDraft.collected_date = null;
      
      this.$root.task.date = null;
      delete this.$root.task.active_date_start;
      delete this.$root.task.active_date_end;
      this.$root.task.collected_date = null;
      delete this.$root.task.collected_date_start;
      delete this.$root.task.collected_date_end;
      this.$root.task.created_date = null;
      delete this.$root.task.created_date_start;
      delete this.$root.task.created_date_end;
      
      // Clear search filters
      ['search','sortingOrder','sort'].forEach(k => {
        if (this.$root.task[k] !== undefined) {
          this.$root.task[k] = '';
        }
      });
      
      // Clear search fields from $root.task
      this.$root.task.farmer_name = '';
      this.$root.task.task_number = '';
      this.$root.task.sample_id = '';
      
      // Reset page to 1
      this.$root.task.page = 1;
      
      // Clear searchFields as well to ensure UI is updated
      this.searchFields = {
        farmer_name: '',
        task_number: '',
        sample_id: '',
      };
      
      // Apply filters immediately to refresh the data
      this.applyFiltersWithoutClosing(false);
    },
    clearAllFilters() {
      // Clear regular string filters from both taskDraft (UI bindings) and $root.task (persistence)
      ['filter','user_filter','contractor','task_report_status','soil_report_status','search','sortingOrder','sort'].forEach(k => {
        this.taskDraft[k] = '';
        if (this.$root.task[k] !== undefined) {
          this.$root.task[k] = '';
        }
      });
      
      // Clear array filters (advisor, status) 
      ['advisor', 'status'].forEach(k => {
        this.taskDraft[k] = [];
        if (this.$root.task[k] !== undefined) {
          this.$root.task[k] = [];
        }
      });
      
      // Clear date filters from both taskDraft and $root.task
      this.taskDraft.date = null;
      this.taskDraft.created_date = null;
      this.taskDraft.collected_date = null;
      // clear other date ranges
      this.$root.task.date = null;
      delete this.$root.task.active_date_start;
      delete this.$root.task.active_date_end;
      this.$root.task.collected_date = null;
      delete this.$root.task.collected_date_start;
      delete this.$root.task.collected_date_end;
      
      // Clear created date filter
      this.$root.task.created_date = null;
      delete this.$root.task.created_date_start;
      delete this.$root.task.created_date_end;
      
      // Clear search fields
      this.searchFields = {
        farmer_name: '',
        task_number: '',
        sample_id: '',
      };
      
      // Clear search fields from $root.task as well
      this.$root.task.farmer_name = '';
      this.$root.task.task_number = '';
      this.$root.task.sample_id = '';
      
      // pagination reset
      this.$root.task.page = 1;
      
      // If we're in issue mode (inbox), refresh the issues view with cleared filters
      if (this.issueMode) {
        this.loadIssuesTasks();
      } else {
        // Reset filtered issues count when not in issue mode
        this.filteredIssuesCount = null;
      }
      
      // perform refresh with default created date filter
      this.applyFiltersWithoutClosing();
    },

    

    searching: _.debounce(function () {
      this.$root.task.page = 1;
    }, 750),
    
    // Search field methods
    handleSearchFieldChange() {
      this.performSearchOnly();
    },
    
    performSearchOnly() {
      // Store search fields in root state to persist across re-renders
      this.$root.searchFields = { ...this.searchFields };
      
      // Update root state with search parameters
      Object.assign(this.$root.task, {
        farmer_name: this.searchFields.farmer_name,
        task_number: this.searchFields.task_number,
        sample_id: this.searchFields.sample_id,
        page: 1, // Reset to first page
      });
      
      // Apply the current filters and search without closing drawer
      // Pass true to sync taskDraft since user changed filters
      this.applyFiltersWithoutClosing(true);
    },
    
    applyFiltersWithoutClosing(shouldSyncTaskDraft = false) {
      
      // Only sync taskDraft to root task when explicitly requested (e.g., from filter changes)
      // This prevents pagination/sorting from losing filters
      if (shouldSyncTaskDraft) {
        // Sync all fields from taskDraft, including empty values (user clearing a filter)
        // This ensures when user deselects a filter (sets it to ''), it gets cleared from $root.task
        Object.keys(this.taskDraft).forEach(key => {
          // Always sync the value, even if empty (unless it's an empty array)
          if (Array.isArray(this.taskDraft[key])) {
            // For arrays, only skip if empty
            if (this.taskDraft[key].length > 0) {
              this.$root.task[key] = this.taskDraft[key];
            } else {
              // Clear array filter
              this.$root.task[key] = [];
            }
          } else {
            // For non-arrays (strings, numbers), always sync including empty strings
            this.$root.task[key] = this.taskDraft[key];
          }
        });
        
      } else {
        
      }
      
      const payload = { ...this.$root.task };
      delete payload.date;
      delete payload.created_date;
      delete payload.collected_date;

      // Handle date filters (same logic as applyTaskFilters)
      if (this.$root.task.date) {
        if (this.dateModes.active === 'month') {
          const rng = expandMonth(this.$root.task.date);
          if (rng) {
            payload.active_date_start = toYMD(rng[0]);
            payload.active_date_end = toYMD(rng[1]);
          }
        } else if (Array.isArray(this.$root.task.date)) {
          payload.active_date_start = toYMD(this.$root.task.date[0]);
          payload.active_date_end = toYMD(this.$root.task.date[1]);
        }
      }
      
      if (this.$root.task.created_date) {
        if (this.dateModes.created === 'month') {
          const rng = expandMonth(this.$root.task.created_date);
          if (rng) {
            payload.created_date_start = toYMD(rng[0]);
            payload.created_date_end = toYMD(rng[1]);
          }
        } else if (Array.isArray(this.$root.task.created_date)) {
          payload.created_date_start = toYMD(this.$root.task.created_date[0]);
          payload.created_date_end = toYMD(this.$root.task.created_date[1]);
        }
      }
      
      if (this.$root.task.collected_date) {
        if (this.dateModes.collected === 'month') {
          const rng = expandMonth(this.$root.task.collected_date);
          if (rng) {
            payload.collected_date_start = toYMD(rng[0]);
            payload.collected_date_end = toYMD(rng[1]);
          }
        } else if (Array.isArray(this.$root.task.collected_date)) {
          payload.collected_date_start = toYMD(this.$root.task.collected_date[0]);
          payload.collected_date_end = toYMD(this.$root.task.collected_date[1]);
        }
      }
      
      // Search fields are already included in payload from $root.task (farmer_name, task_number, sample_id)
      // No need to add them separately as they come from performSearchOnly()
      
      // Clean up empty values and handle array parameters
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
          delete payload[key];
        }
      });
      
      // Convert arrays to proper Laravel array format for multi-select filters
      const processedPayload = { ...payload };
      ['advisor', 'status'].forEach(key => {
        if (Array.isArray(processedPayload[key]) && processedPayload[key].length > 0) {
          // Laravel expects array parameters in bracket notation: advisor[], advisor[], etc.
          // Axios will automatically handle this when we pass an array
          processedPayload[key] = processedPayload[key];
        } else if (Array.isArray(processedPayload[key]) && processedPayload[key].length === 0) {
          // Remove empty arrays
          delete processedPayload[key];
        }
      });
      
      // Use axios GET request to avoid URL parameter pollution while keeping clean internal state
      axios.get(route('app.task.index'), { params: processedPayload })
        .then(response => {
          
          // Parse the HTML response to extract the tasks data
          const parser = new DOMParser();
          const doc = parser.parseFromString(response.data, 'text/html');
          const pageDataScript = doc.querySelector('div[data-page]');
          
          if (pageDataScript) {
            const pageData = JSON.parse(pageDataScript.dataset.page);
            
            // Update local state with new filtered data from response
            this.tasks = pageData.props.results.original.data;
            this.links = pageData.props.results.original.meta.pagination;
            // Sync dashboard stats from response
            this.localDashboardStats = pageData.props.dashboardStats;
          } else {
            console.error('Failed to parse page data from response');
          }
        })
        .catch(error => {
          console.error('Error fetching filtered tasks:', error);
        });
    },
    
    clearAllSearches() {
      this.searchFields = {
        farmer_name: '',
        task_number: '',
        sample_id: '',
      };
      this.performSearchOnly();
    },
    
    handleFilterChange() {
      // Apply filters instantly when they change
      // Pass true to sync taskDraft since user changed filters
      // Reset page to 1 when filters change - new filters may have different result counts
      this.$root.task.page = 1;
      
      this.applyFiltersWithoutClosing(true);
    },

    // Handle pagination without URL updates
    // Handle pagination without URL updates
    handlePageChange(page) {
      
      // Update page in root state
      this.$root.task.page = page;
      
      // Apply filters to get new page data
      this.applyFiltersWithoutClosing();
    },

    async closeAllTaskIssues(task) {
      const response = await this.$swal.fire({
        title: "Warning",
        text: "This will permanently remove this issue from the Issues list, do you want to proceed?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1E660B",
        cancelButtonColor: "#d33",
      });

      if (response.isConfirmed) {
        this.loading = true;
        try {
          const request = await axios.post(route("app.task.issue.close", task.id));
          this.loadIssuesTasks();
          this.toast.success(request.data.message);
        } catch (error) {
          this.toast.error("Apologies, but please try again later.");
        }
      }
    },

    reset() { // keeping legacy reference (now acts same as clearSort when no explicit clear link)
      this.clearSort();
    },

    filtering() {
      this.$root.task.search = "";
      this.$root.task.page = 1;

      if (this.$root.task.sort != "my_farmers") {
        this.$root.task.consultant = "";
      } else {
        this.$root.task.consultant = this.$page.props.auth.user.id;
      }

      const payload = {
        ...this.$root.task,
      };
      delete payload.date;
      delete payload.created_date;
      delete payload.collected_date;
      if (this.$root.task.date) {
        if (this.dateModes.active === 'month') {
          const rng = expandMonth(this.$root.task.date);
          if (rng) { payload.active_date_start = toYMD(rng[0]); payload.active_date_end = toYMD(rng[1]); }
        } else if (Array.isArray(this.$root.task.date)) {
          payload.active_date_start = toYMD(this.$root.task.date[0]);
          payload.active_date_end = toYMD(this.$root.task.date[1]);
        }
      }
      if (this.$root.task.created_date) {
        if (this.dateModes.created === 'month') {
          const rng = expandMonth(this.$root.task.created_date);
          if (rng) { payload.created_date_start = toYMD(rng[0]); payload.created_date_end = toYMD(rng[1]); }
        } else if (Array.isArray(this.$root.task.created_date)) {
          payload.created_date_start = toYMD(this.$root.task.created_date[0]);
          payload.created_date_end = toYMD(this.$root.task.created_date[1]);
        }
      }
      if (this.$root.task.collected_date) {
        if (this.dateModes.collected === 'month') {
          const rng = expandMonth(this.$root.task.collected_date);
          if (rng) { payload.collected_date_start = toYMD(rng[0]); payload.collected_date_end = toYMD(rng[1]); }
        } else if (Array.isArray(this.$root.task.collected_date)) {
          payload.collected_date_start = toYMD(this.$root.task.collected_date[0]);
          payload.collected_date_end = toYMD(this.$root.task.collected_date[1]);
        }
      }
      
      // Convert arrays to proper Laravel array format for multi-select filters
      const processedPayload = { ...payload };
      ['advisor', 'status'].forEach(key => {
        if (Array.isArray(processedPayload[key]) && processedPayload[key].length > 0) {
          // For Inertia, arrays should be passed as is - Inertia handles the serialization
          processedPayload[key] = processedPayload[key];
        } else if (Array.isArray(processedPayload[key]) && processedPayload[key].length === 0) {
          // Remove empty arrays
          delete processedPayload[key];
        }
      });
      
      this.$inertia.get(route("app.task.index"), processedPayload);
    },

    indexLinks(task) {
      if (
        (this.$page.props.auth.user?.organisation?.type == "lab" &&
          this.$page.props.auth.user.id != task.created_by &&
          !(["consultant", "consultant_user", "lab_user"].includes(this.$page.props.auth.user.role) && this.$page.props.auth.user.organisation_id == task.organisation_id)) ||
        (["consultant", "consultant_user"].includes(
          this.$page.props.auth.user.role
        ) &&
          this.$page.props.auth.user.organisation_id != task.organisation_id) ||
        (this.$page.props.auth.user.role == "contractor" &&
          this.$page.props.auth.user.organisation_id !=
            task.assigned_to_organisation) ||
        (this.$page.props.auth.user.role == "soil_sampler" &&
          this.$page.props.auth.user.id != task.assigned_to_user)
      ) {
        return {
          "View Task": route("app.task.view", {
            task: task.id,
            tab: "records",
          }),
        };
      } else {
        return {
          "View Task": route("app.task.view", {
            task: task.id,
            tab: "records",
          }),
          "Edit Task": route("app.task.edit", task.id),
        };
      }
    },

    loadTaskWithIssues() {
      this.loading = true;
      this.issueMode = !this.issueMode;

      if (this.issueMode) {
        // Ensure we're on the tasks tab to see the notifications inbox
        this.activeTab = 'tasks';
        this.loadIssuesTasks();
      } else {
        // Reset filtered issues count when leaving issue mode
        this.filteredIssuesCount = null;
        this.loading = false;
      }
    },

    handlePaginatingData(endpoint) {
      this.loading = true;
      this.loadIssuesTasks(endpoint);
    },

    displayIssuesDescriptions(allIssue) {
      let issues = [];

      allIssue.forEach((issue) => {
        if (issue.status == "open") {
          switch (issue.type) {
            case "sample_zone_issues":
              issues.push("Matching issues");
              break;

            case "sample_path_issues":
              issues.push("Sample Issues");
              break;

            case "rejected":
              issues.push(issue.description?.[0]);
              break;

            case "logsheet_partial":
              Object.values(issue.description).forEach((error) => {
                issues.push(error?.[0]);
              });
              break;

            case "logsheet":
              Object.values(issue.description).forEach((error) => {
                const error_single = error?.[0];
                //check if is a LPIS CODE missing error
                if (error_single.includes("lpisLabel")) {
                  if (!issues.includes("LPIS Matching Issue")) {
                    issues.push("LPIS Matching Issue");
                  }
                } else {
                  issues.push(error?.[0]);
                }
              });
              break;

            case "isec_lims":
            case "lab_account_missing":
            case "lab_test_unmapped":
              Object.values(issue.description).forEach((error) => {
                issues.push(error);
              });
              break;

            default:
              break;
          }
        }
      });

      return issues;
    },

    sorting(sort) {
      // Initialize by param if missing
      if (!this.$root.task.by) {
        this.$root.task.by = 'asc';
      }
      if (this.$root.task.sort === sort) {
        this.$root.task.by = this.$root.task.by === 'asc' ? 'desc' : 'asc';
      } else {
        this.$root.task.sort = sort;
        this.$root.task.by = 'asc';
      }
      this.$root.task.page = 1;
      this.applyFiltersWithoutClosing();
    },
    taskTypeLabel(task){
      // Attempt to derive a human label for task type; fallback to generic Soil Sampling
      // Common possible keys: survey_name, name, type
      const candidates = [task.survey_name, task.name, task.type];
      const label = candidates.find(v => typeof v === 'string' && v.trim().length) || 'Soil Sampling';
      return label;
    },
    clearSort() {
      if (this.$root.task.sort) delete this.$root.task.sort;
      if (this.$root.task.by) delete this.$root.task.by;
      if (this.$root.task.sortingOrder !== undefined) delete this.$root.task.sortingOrder; // legacy cleanup
      this.$root.task.page = 1;
      this.applyFiltersWithoutClosing();
    },

    loadIssuesTasks(endpoint = route("app.task.index.issue")) {
      let vm = this;
      
      // Build query parameters, including all current filters to match main task view
      let params = {};
      
      // Include existing filters (only if they have values)
      if (vm.$root.task && vm.$root.task.advisor && typeof vm.$root.task.advisor === 'string' && vm.$root.task.advisor.trim() !== '') {
        params.advisor = vm.$root.task.advisor;
      }
      if (vm.$root.task && vm.$root.task.filter && typeof vm.$root.task.filter === 'string' && vm.$root.task.filter.trim() !== '') {
        params.filter = vm.$root.task.filter;
      }
      if (vm.$root.task && vm.$root.task.user_filter && typeof vm.$root.task.user_filter === 'string' && vm.$root.task.user_filter.trim() !== '') {
        params.user_filter = vm.$root.task.user_filter;
      }
      if (vm.$root.task && vm.$root.task.contractor && typeof vm.$root.task.contractor === 'string' && vm.$root.task.contractor.trim() !== '') {
        params.contractor = vm.$root.task.contractor;
      }
      if (vm.$root.task && vm.$root.task.search && typeof vm.$root.task.search === 'string' && vm.$root.task.search.trim() !== '') {
        params.search = vm.$root.task.search;
      }
      if (vm.$root.task && vm.$root.task.status && typeof vm.$root.task.status === 'string' && vm.$root.task.status.trim() !== '') {
        params.status = vm.$root.task.status;
      }

      // Handle active date filter
      if (vm.$root.task && vm.$root.task.date) {
        if (vm.dateModes.active === 'month') {
          const rng = expandMonth(vm.$root.task.date);
          if (rng) {
            const startDate = toYMD(rng[0]);
            const endDate = toYMD(rng[1]);
            if (startDate && endDate) {
              params.active_date_start = startDate;
              params.active_date_end = endDate;
            }
          }
        } else if (Array.isArray(vm.$root.task.date)) {
          const startDate = toYMD(vm.$root.task.date[0]);
          const endDate = toYMD(vm.$root.task.date[1]);
          if (startDate && endDate) {
            params.active_date_start = startDate;
            params.active_date_end = endDate;
          }
        }
      }
      
      // Handle created date filter
      if (vm.$root.task && vm.$root.task.created_date) {
        if (vm.dateModes.created === 'month') {
          const rng = expandMonth(vm.$root.task.created_date);
          if (rng) {
            const startDate = toYMD(rng[0]);
            const endDate = toYMD(rng[1]);
            if (startDate && endDate) {
              params.created_date_start = startDate;
              params.created_date_end = endDate;
            }
          }
        } else if (Array.isArray(vm.$root.task.created_date)) {
          const startDate = toYMD(vm.$root.task.created_date[0]);
          const endDate = toYMD(vm.$root.task.created_date[1]);
          if (startDate && endDate) {
            params.created_date_start = startDate;
            params.created_date_end = endDate;
          }
        }
      }
      
      // Handle collected date filter
      if (vm.$root.task && vm.$root.task.collected_date) {
        if (vm.dateModes.collected === 'month') {
          const rng = expandMonth(vm.$root.task.collected_date);
          if (rng) {
            const startDate = toYMD(rng[0]);
            const endDate = toYMD(rng[1]);
            if (startDate && endDate) {
              params.collected_date_start = startDate;
              params.collected_date_end = endDate;
            }
          }
        } else if (Array.isArray(vm.$root.task.collected_date)) {
          const startDate = toYMD(vm.$root.task.collected_date[0]);
          const endDate = toYMD(vm.$root.task.collected_date[1]);
          if (startDate && endDate) {
            params.collected_date_start = startDate;
            params.collected_date_end = endDate;
          }
        }
      }
      
      // If endpoint is the base route, add params as query string
      if (endpoint === route("app.task.index.issue") && Object.keys(params).length > 0) {
        const queryString = new URLSearchParams(params).toString();
        endpoint = `${endpoint}?${queryString}`;
      }
      
      axios.get(endpoint)
        .then(function (response) {
          // Normalise Fractal/Dingo style response vs plain JSON to a consistent shape
          // Expected final shape for tasksWithIssues: { data: [...], meta: { pagination: {...} } }
          let raw = response.data?.results;
          // Helper to safely fetch nested keys
          const dig = (obj, path) => path.split('.').reduce((o,k)=> (o && o[k] !== undefined) ? o[k] : undefined, obj);
          // Candidate locations for data array
          const candidates = [
            dig(raw, 'original.data.data'),    // results.original.data.data
            dig(raw, 'original.data'),         // results.original.data
            dig(raw, 'original.original.data'), // double wrapped
            dig(raw, 'data.data'),             // results.data.data
            dig(raw, 'data'),                  // results.data
            Array.isArray(raw) ? raw : undefined
          ].filter(Boolean);
          const dataArray = candidates.find(c => Array.isArray(c)) || [];
          // Pagination meta candidates
          const metaCandidates = [
            dig(raw, 'original.meta'),
            dig(raw, 'meta'),
            dig(raw, 'original.original.meta'),
          ].filter(Boolean);
          const meta = metaCandidates.find(m => typeof m === 'object') || { pagination: null };
          vm.tasksWithIssues = { data: dataArray, meta };
          vm.tasksIssuesLink = response.data.paginate;
          
          // Update filtered issues count for notification badge
          if (meta && meta.pagination && typeof meta.pagination.total !== 'undefined') {
            vm.filteredIssuesCount = meta.pagination.total;
          } else if (meta && typeof meta.total !== 'undefined') {
            // Fallback to direct meta.total if pagination.total is not available
            vm.filteredIssuesCount = meta.total;
          }
          
          if (!dataArray.length) {
            console.warn('Issues Inbox: no data extracted. Raw response shape:', raw);
            vm.tasksWithIssues.debug_empty = true;
          }
        })
        .catch(function (error) {
          console.error('Failed loading tasks with issues', error);
          vm.tasksWithIssues = { data: [], meta: { pagination: null }, debug_error: true };
          if (vm.toast) vm.toast.error('Unable to load Inbox issues right now.');
        })
        .finally(function(){
          vm.loading = false;
        });
    },

    selectFarm(index, farmer) {
      this.$root.search.farmer.selected = index;
      this.$root.search.farmer.search = farmer;

      let vm = this;

      axios.post(route("app.farmer.map.view", index)).then(function (response) {
        vm.$root.farmer = response.data;
      });
    },
    // Legacy single formatter no longer used directly (kept for safety if referenced elsewhere)
    format(value){ return this._formatDatePicker(value, 'active'); },
    getContractor() {
      let search = {
        contractor: "",
        search: "",
      };

      let vm = this;

      axios
        .get(route("app.get-all-contractors", search))
        .then(function (response) {
          vm.contractors = response.data.contractors;
        });
    },
    generateCSV() {
      // this.showCsvMenu = false; // keep menu open during generation
      this.csvCreating = true;
      this.csvAvailable = false;
      this.csvRequested = true;
      // Immediately close the menu so it doesn't linger while we poll for availability
      this.showCsvMenu = false;
      
      // Create payload exactly like filtering() method but preserve search
      const payload = {
        ...this.$root.task,
      };
      
      // NOTE: Unlike filtering() we don't clear search - we want CSV to respect current search
      payload.page = 1;

      // Handle consultant assignment logic (same as filtering method)
      if (this.$root.task.sort != "my_farmers") {
        payload.consultant = "";
      } else {
        payload.consultant = this.$page.props.auth.user.id;
      }
      
      // Process date filters the same way as filtering() and applyTaskFilters()
      delete payload.date;
      delete payload.created_date;
      delete payload.collected_date;

      // Handle active date filter
      if (this.$root.task.date) {
        if (this.dateModes.active === 'month') {
          const rng = expandMonth(this.$root.task.date);
          if (rng) {
            payload.active_date_start = toYMD(rng[0]);
            payload.active_date_end = toYMD(rng[1]);
          }
        } else if (Array.isArray(this.$root.task.date)) {
          payload.active_date_start = toYMD(this.$root.task.date[0]);
          payload.active_date_end = toYMD(this.$root.task.date[1]);
        }
      }
      
      // Handle created date filter
      if (this.$root.task.created_date) {
        if (this.dateModes.created === 'month') {
          const rng = expandMonth(this.$root.task.created_date);
          if (rng) {
            payload.created_date_start = toYMD(rng[0]);
            payload.created_date_end = toYMD(rng[1]);
          }
        } else if (Array.isArray(this.$root.task.created_date)) {
          payload.created_date_start = toYMD(this.$root.task.created_date[0]);
          payload.created_date_end = toYMD(this.$root.task.created_date[1]);
        }
      }
      
      // Handle collected date filter
      if (this.$root.task.collected_date) {
        if (this.dateModes.collected === 'month') {
          const rng = expandMonth(this.$root.task.collected_date);
          if (rng) {
            payload.collected_date_start = toYMD(rng[0]);
            payload.collected_date_end = toYMD(rng[1]);
          }
        } else if (Array.isArray(this.$root.task.collected_date)) {
          payload.collected_date_start = toYMD(this.$root.task.collected_date[0]);
          payload.collected_date_end = toYMD(this.$root.task.collected_date[1]);
        }
      }

      // Clean up any undefined, null, or empty string values that might interfere
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
          delete payload[key];
        }
      });

      // Convert arrays to proper Laravel array format for multi-select filters
      const processedPayload = { ...payload };
      ['advisor', 'status'].forEach(key => {
        if (Array.isArray(processedPayload[key]) && processedPayload[key].length > 0) {
          // Laravel expects array parameters in bracket notation: advisor[], advisor[], etc.
          // Axios will automatically handle this when we pass an array
          processedPayload[key] = processedPayload[key];
        } else if (Array.isArray(processedPayload[key]) && processedPayload[key].length === 0) {
          // Remove empty arrays
          delete processedPayload[key];
        }
      });

      axios
        .get(route("app.task.create-index-csv"), { params: processedPayload }, {
          responseType: "json", // Changed from blob to json since we're getting a status response
          timeout: 120000, // Increased timeout to 2 minutes for large datasets
        })
        .then((response) => {
          this.toast.success("Preparing CSV export... Large datasets may take a few minutes to process.");
          this.checkCsvAvailibility();
        })
        .catch((error) => {
          console.error("CSV generation error:", error);
          if (error.code === 'ECONNABORTED') {
            this.toast.error("Request timeout. Large datasets are still being processed in the background. Please wait and try downloading again in a few minutes.");
            this.checkCsvAvailibility(); // Still check for availability in case it's processing
          } else {
            this.toast.error("Failed to start CSV generation. Please try again.");
          }
        })
        .finally(() => {
          this.csvCreating = false;
        });
    },
    checkCsvAvailibility() {
      if (!this.csvRequested) {
        return; // only poll after an explicit export request
      }
      let vm = this;
      this.csvAvailable = false;
      let pollCount = 0;
      const maxPolls = 40; // Poll for up to 2 minutes (40 * 3 seconds)
      
      const checkAvailability = () => {
        pollCount++;
        axios
          .get(route("app.task.index-csv-availability"))
          .then((response) => {
            if (response.data.status == "success") {
              vm.csvAvailable = true;
              clearInterval(vm.csvAvailabilityTimer);
              // Auto-download once available
              vm.downloadCSV();
              vm.showCsvMenu = false; // close after auto-download
              vm.toast.success("CSV export completed and ready for download!");
            } else if (pollCount >= maxPolls) {
              // Stop polling after max attempts
              clearInterval(vm.csvAvailabilityTimer);
              vm.csvCreating = false;
              vm.toast.warning("CSV export is taking longer than expected. Please try downloading manually in a few minutes.");
            }
          })
          .catch((error) => {
            if (error.response && error.response.status !== 404) {
              clearInterval(vm.csvAvailabilityTimer);
              vm.csvCreating = false;
              this.toast.error(
                error.response.data.message ||
                  "Failed to check CSV availability"
              );
            } else if (pollCount >= maxPolls) {
              // Stop polling after max attempts even on 404s
              clearInterval(vm.csvAvailabilityTimer);
              vm.csvCreating = false;
              vm.toast.warning("CSV export is taking longer than expected. Please check back in a few minutes.");
            }
          });
      };
      
      checkAvailability();
      this.csvAvailabilityTimer = setInterval(checkAvailability, 3000);
    },
    downloadCSV() {
      if (!this.csvAvailable) return; // guard
      window.location.href = route('app.task.download-index-csv');
    },
    
    // Removed loadReportStatus + _fetchReportsIndividually (server supplies aggregates)

    hasReports(task) {
      return ['task_report_overall','soil_report_overall'].some(k => {
        const v = task[k];
        return v && v !== 'none';
      }) || task.combine_task_report || task.combine_soil_analysis_report;
    },

    getTaskReportClass(task) {
      const status = task.task_report_overall;
      switch(status){
        case 'all_sent': return 'bg-green-500';
        case 'partial_sent':
        case 'all_generated':
        case 'partial_generated': return 'bg-yellow-500';
        default: return 'bg-gray-300';
      }
    },

    getSoilAnalysisClass(task) {
      const status = task.soil_report_overall;
      switch(status){
        case 'all_sent': return 'bg-green-500';
        case 'partial_sent':
        case 'all_generated':
        case 'partial_generated': return 'bg-yellow-500';
        default: return 'bg-gray-300';
      }
    },

    getReportsTooltip(task) {
      const total = task.report_tests_total || 0;
      const lines = [];
      const mapLine = (label, overall, gen, email) => {
        if (!overall || overall === 'none') return `${label}: Not Generated`;
        switch(overall){
          case 'partial_generated': return `${label}: ${gen}/${total} Generated`;
          case 'all_generated': return `${label}: All Generated, 0 Sent`;
          case 'partial_sent': return `${label}: All Generated, ${email}/${total} Sent`;
          case 'all_sent': return `${label}: All Sent`;
          default: return `${label}: Not Generated`;
        }
      };
      lines.push(mapLine('Task Report', task.task_report_overall, task.task_reports_generated_count, task.task_reports_emailed_count));
      lines.push(mapLine('Soil Analysis', task.soil_report_overall, task.soil_reports_generated_count, task.soil_reports_emailed_count));
      return lines.join('\n');
    },
    statusPillClass(task){
      // task.status_class already supplies bg/text colors; augment with pill utility classes
      return [
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border border-black/5 shadow-sm',
        task.status_class || 'bg-gray-100 text-gray-800'
      ];
    },

    // Previous month range now provided via date picker presetRanges (see computed.presetRanges)
    // applyPrevMonthRange no longer needed (superseded by integrated presets in SmartDateRangePicker)
    // Removed old inline month picker logic (now handled by SmartDateRangePicker component)
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    setDateMode(which, mode){
      if(!this.dateModes[which]) return;
      const keyMap = { active: 'date', created: 'created_date', collected: 'collected_date' };
      const k = keyMap[which];
      if (!k) return;
      const current = this.taskDraft[k];
      if (mode === 'month') {
        // Convert existing range array to month object (use start date)
        if (Array.isArray(current) && current[0] instanceof Date) {
          this.taskDraft[k] = { month: current[0].getMonth(), year: current[0].getFullYear() };
        } else if (current instanceof Date) {
          this.taskDraft[k] = { month: current.getMonth(), year: current.getFullYear() };
        } else if (!current) {
          const now = new Date();
          this.taskDraft[k] = { month: now.getMonth(), year: now.getFullYear() };
        }
      } else if (mode === 'range') {
        // Convert month object to full month boundary array
        if (isMonthObj(current)) {
          this.taskDraft[k] = expandMonth(current);
        }
      }
      this.dateModes[which] = mode;
    },
    applyPreset(which, range){
      const keyMap = { active: 'date', created: 'created_date', collected: 'collected_date' };
      const k = keyMap[which];
      if(!k) return;
      
      // Handle "No Range" preset (null range)
      if (!range) {
        this.taskDraft[k] = null;
        return;
      }
      
      if (this.dateModes[which] === 'month') {
        // Use first date's month/year
        const d = range[0];
        this.taskDraft[k] = { month: d.getMonth(), year: d.getFullYear() };
      } else {
        // clone to avoid reactive anomalies with shared Date objects
        this.taskDraft[k] = range.map(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      }
    },
    isPresetActive(which, preset){
      const keyMap = { active: 'date', created: 'created_date', collected: 'collected_date' };
      const k = keyMap[which];
      const cur = this.taskDraft[k];
      
      // Handle null preset.range (like "No Range")
      if (!preset.range) {
        return !cur; // Active if current filter is also null/empty
      }
      
      if (!cur) return false;
      
      const [c,d] = preset.range;
      if (this.dateModes[which] === 'month') {
        if (!isMonthObj(cur)) return false;
        return cur.month === c.getMonth() && cur.year === c.getFullYear();
      }
      if(!Array.isArray(cur) || cur.length !== 2) return false;
      const [a,b] = cur;
      return a instanceof Date && b instanceof Date && a.getTime() === c.getTime() && b.getTime() === d.getTime();
    },
    // INTERNAL: compute progress for either 'task' or 'soil'
    // Removed legacy progress helpers (now server-provided aggregates)
  },
  

  
  computed: {
    // Control when to show tasks on mobile (based on active tab) and always show on desktop
    showTasksOnMobile() {
      // On desktop (md and up), always show tasks
      // On mobile, only show when activeTab is 'tasks'
      if (typeof window !== 'undefined' && window.innerWidth >= 768) {
        return true; // Always show on desktop
      }
      return this.activeTab === 'tasks'; // Only show on mobile when tasks tab is active
    },
    // Transform advisors object into format suitable for select
    advisorOptions() {
      if (!this.advisors || typeof this.advisors !== 'object') {
        return [];
      }
      // Convert {id: name, ...} to [{value: id, label: name}, ...] and ensure alphabetical order
      return Object.entries(this.advisors)
        .map(([id, name]) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    // Filter advisor options based on search
    filteredAdvisorOptions() {
      if (!this.advisorSearch) {
        return this.advisorOptions;
      }
      return this.advisorOptions.filter(option =>
        option.label.toLowerCase().includes(this.advisorSearch.toLowerCase())
      );
    },
    // Get selected advisor name for display (single select compatibility)
    selectedAdvisorName() {
      if (!this.taskDraft.advisor || this.taskDraft.advisor.length === 0) return null;
      const selected = this.advisorOptions.find(opt => opt.value === this.taskDraft.advisor[0]);
      return selected ? selected.label : null;
    },
    // Get selected advisor names for display (multi-select)
    selectedAdvisorNames() {
      if (!this.taskDraft.advisor || this.taskDraft.advisor.length === 0) return [];
      return this.taskDraft.advisor.map(advisorId => {
        const selected = this.advisorOptions.find(opt => opt.value === advisorId);
        return selected ? selected.label : '';
      }).filter(name => name);
    },
    // Transform contractors object into format suitable for select
    contractorOptions() {
      if (!this.contractors || typeof this.contractors !== 'object') {
        return [];
      }
      // Convert {id: name, ...} to [{value: id, label: name}, ...] and ensure alphabetical order
      return Object.entries(this.contractors)
        .map(([id, name]) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    // Filter contractor options based on search
    filteredContractorOptions() {
      if (!this.contractorSearch) {
        return this.contractorOptions;
      }
      return this.contractorOptions.filter(option =>
        option.label.toLowerCase().includes(this.contractorSearch.toLowerCase())
      );
    },
    // Get selected contractor name for display
    selectedContractorName() {
      if (!this.taskDraft.contractor) return null;
      const selected = this.contractorOptions.find(opt => opt.value === this.taskDraft.contractor);
      return selected ? selected.label : null;
    },
    // Transform statuses object into format suitable for select
    statusOptions() {
      if (!this.statuses || typeof this.statuses !== 'object') {
        return [];
      }
      return Object.entries(this.statuses)
        .map(([key, name]) => ({ value: key, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    // Filter status options based on search
    filteredStatusOptions() {
      if (!this.statusSearch) {
        return this.statusOptions;
      }
      return this.statusOptions.filter(option =>
        option.label.toLowerCase().includes(this.statusSearch.toLowerCase())
      );
    },
    // Get selected status names for display (multi-select)
    selectedStatusNames() {
      if (!this.taskDraft.status || this.taskDraft.status.length === 0) return [];
      return this.taskDraft.status.map(statusKey => {
        const selected = this.statusOptions.find(opt => opt.value === statusKey);
        return selected ? selected.label : '';
      }).filter(name => name);
    },
    // Transform statuses object into format suitable for select
    statusOptions() {
      if (!this.statuses || typeof this.statuses !== 'object') {
        return [];
      }
      return Object.entries(this.statuses)
        .map(([key, name]) => ({ value: key, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    // Filter status options based on search
    filteredStatusOptions() {
      if (!this.statusSearch) {
        return this.statusOptions;
      }
      return this.statusOptions.filter(option =>
        option.label.toLowerCase().includes(this.statusSearch.toLowerCase())
      );
    },
    // Get selected status names for display (multi-select)
    selectedStatusNames() {
      if (!this.taskDraft.status || this.taskDraft.status.length === 0) return [];
      return this.taskDraft.status.map(statusKey => {
        const selected = this.statusOptions.find(opt => opt.value === statusKey);
        return selected ? selected.label : '';
      }).filter(name => name);
    },
    activeFilterCount() {
      // Count all active filters, being very precise about what counts as "active"
      let count = 0;
      
      // Only count non-empty string filters
      const stringFilters = ['filter','user_filter','contractor','task_report_status','soil_report_status'];
      stringFilters.forEach(key => {
        const value = this.$root.task?.[key];
        if (value && typeof value === 'string' && value.trim() !== '') {
          count++;
        }
      });
      
      // Special handling for advisor field (can be array or string)
      const advisorValue = this.$root.task?.advisor;
      if (advisorValue) {
        if (Array.isArray(advisorValue) && advisorValue.length > 0) {
          count++;
        } else if (typeof advisorValue === 'string' && advisorValue.trim() !== '') {
          count++;
        }
      }
      
      // Special handling for status field (can be array or string)
      const statusValue = this.$root.task?.status;
      if (statusValue) {
        if (Array.isArray(statusValue) && statusValue.length > 0) {
          count++;
        } else if (typeof statusValue === 'string' && statusValue.trim() !== '') {
          count++;
        }
      }
      
      // Count active date range (but only if it has valid dates and isn't default)
      const isRange = (v) => Array.isArray(v) && v.length === 2;
      
      // Count active date filter only if it's a real filter (not empty)
      if (this.$root.task?.date && (isRange(this.$root.task.date) || isMonthObj(this.$root.task.date))) {
        count++;
      }
      
      // Count created_date filter
      if (this.$root.task?.created_date && (isRange(this.$root.task.created_date) || isMonthObj(this.$root.task.created_date))) {
        count++;
      }
      
      // Count collected_date filter
      if (this.$root.task?.collected_date && (isRange(this.$root.task.collected_date) || isMonthObj(this.$root.task.collected_date))) {
        count++;
      }
      
      return count;
    },
    
    // Search-related computed properties
    hasActiveSearches() {
      return Object.values(this.searchFields).some(value => value && value.trim() !== '');
    },
    
    activeSearchCount() {
      return Object.values(this.searchFields).filter(value => value && value.trim() !== '').length;
    },
    
    totalActiveCount() {
      return this.activeFilterCount + this.activeSearchCount;
    },
    datePresets(){
      const today = new Date();
      const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfThisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const startPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      const last30Start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29); // include today => 30 days
      const last60Start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 59); // include today => 60 days
      const last90Start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 89); // include today => 90 days
      const ytdStart = new Date(today.getFullYear(), 0, 1);
      // Normalize times to midnight to keep comparisons clean
      const norm = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
      return [
        { label: 'This Month', range: [norm(startOfThisMonth), norm(endOfThisMonth)] },
        { label: 'Previous Month', range: [norm(startPrevMonth), norm(endPrevMonth)] },
        { label: 'Last 30 Days', range: [norm(last30Start), norm(today)] },
        { label: 'Last 60 Days', range: [norm(last60Start), norm(today)] },
        { label: 'Last 90 Days', range: [norm(last90Start), norm(today)] },
        { label: 'Year to Date', range: [norm(ytdStart), norm(today)] },
        { label: 'All Time', range: null },
      ];
},
    
    currentCreatedDateLabel() {
      if (!this.$root.task || !this.$root.task.created_date) {
        return 'All Time';
      }
      
      const createdDate = this.$root.task.created_date;
      
      // Check if it matches a preset
      const preset = this.datePresets.find(p => {
        if (!p.range || !Array.isArray(createdDate) || createdDate.length !== 2) return false;
        const [start, end] = createdDate;
        const [presetStart, presetEnd] = p.range;
        return start.getTime() === presetStart.getTime() && end.getTime() === presetEnd.getTime();
      });
      
      if (preset) {
        return preset.label;
      }
      
      // Format the date range manually
      return this.formatCreated(createdDate);
    },
    
    // Use filtered count if available, otherwise fall back to the original totalIssues
    displayedIssuesCount() {
      return this.filteredIssuesCount !== null ? this.filteredIssuesCount : this.totalIssues;
    },
  },
  beforeDestroy() {
    clearInterval(this.csvAvailabilityTimer);
    
    // Hide task pins from the map when navigating away from the task index page
    this.$emitter.emit('hideTaskPins');
  },
};
</script>
